import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import "./Interviews.scss";

const CANDIDATE_API = `${process.env.REACT_APP_API_HR_URL}/candidate/applied/jobpost/all`;
const MOVE_API = `${process.env.REACT_APP_API_HR_URL}/candidate/move-to-interview`;
const HIRING_FLOW_API = `${process.env.REACT_APP_API_HR_URL}/hiring-flow`;
const STATUS_UPDATE_API = `${process.env.REACT_APP_API_HR_URL}/interview-history/update-interview-status`;
const AVAILABLE_SLOTS_API = `${process.env.REACT_APP_API_HR_URL}/interviewer-scheduled-interview-rounds/interview-history`;
const RESCHEDULE_API = `${process.env.REACT_APP_API_HR_URL}/interviewer-scheduled-interview-rounds/reschedule`;

const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
];
const avatarColor = (seed = "") =>
  COLORS[((seed || "").charCodeAt(0) || 0) % COLORS.length];

const getCandidateName = (c) =>
  c.candidate?.name || c.candidateName || "Unknown";
const getCandidateEmail = (c) => c.candidate?.email || c.email || "";
const getCandidateContact = (c) =>
  c.candidate?.contactNumber || c.contactNumber || "";
const getCandidateId = (c) =>
  c.appliedCandidateHistoryId ||
  c.appliedCandidateId ||
  c.recuirtmentCandidateId;

const getJobTitle = (c) =>
  c.jobPostName ||
  c.jobTitle ||
  c.jobPost?.jobTitle ||
  c.job?.title ||
  c.position ||
  "";
const getJobPostId = (c) =>
  c.jobPostId || c.jobPost?.jobPostId || c.job?.id || null;

const getConsultancyName = (c) =>
  c.consultancyName ||
  c.consultancy?.name ||
  c.consultancy?.consultancyName ||
  (c.sources || []).find((s) => s.consultancyName)?.consultancyName ||
  (c.sources || []).find((s) => s.sourceName === "CONSULTANCY")
    ?.consultancyName ||
  null;

const getConsultancyId = (c) =>
  c.consultancyId ||
  c.consultancy?.id ||
  c.consultancy?.consultancyId ||
  (c.sources || []).find((s) => s.consultancyName)?.connectionId ||
  null;

const UNGROUPED_KEY = "__DIRECT__";

const getCandidateStatus = (c) => {
  const iv = (c.interviewStatus || "").toUpperCase();
  if (iv && iv !== "NOT_SCHEDULED") return iv;
  return (c.status || "APPLIED").toUpperCase();
};

const getCandidateRounds = (c) => {
  const rounds = c.interviewRounds || [];
  return rounds.map((r, i) => ({
    ...r,
    sequence: r.sequence ?? i + 1,
    initial: r.initial ?? i === 0,
    final: r.final ?? i === rounds.length - 1,
  }));
};

const getLatestInterviewHistoryId = (c) => {
  const histories = c.interviewHistories;
  if (histories?.length > 0) {
    const sorted = [...histories].sort(
      (a, b) => (b.interviewHistoryId ?? 0) - (a.interviewHistoryId ?? 0),
    );
    return sorted[0].interviewHistoryId ?? null;
  }
  return c.interviewHistoryId ?? null;
};

const STATUS_MAP = {
  APPLIED: { label: "Applied", cls: "iv-badge--applied" },
  MOVED: { label: "Moved", cls: "iv-badge--moved" },
  NOT_SCHEDULED: { label: "Not Scheduled", cls: "iv-badge--applied" },
  SHORTLISTED: { label: "Shortlisted", cls: "iv-badge--shortlisted" },
  PROCESSING: { label: "Processing", cls: "iv-badge--processing" },
  HOLD: { label: "Hold", cls: "iv-badge--hold" },
  REJECTED: { label: "Rejected", cls: "iv-badge--fail" },
  SELECTED: { label: "Selected", cls: "iv-badge--pass" },
  SCHEDULED: { label: "Scheduled", cls: "iv-badge--moved" },
};

// All statuses available for filter tabs
const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "APPLIED", label: "Applied" },
  { value: "NOT_SCHEDULED", label: "Not Scheduled" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "PROCESSING", label: "Processing" },
  { value: "MOVED", label: "Moved" },
  { value: "SHORTLISTED", label: "Shortlisted" },
  { value: "HOLD", label: "Hold" },
  { value: "SELECTED", label: "Selected" },
  { value: "REJECTED", label: "Rejected" },
];

// Sort options
const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
  { value: "status_asc", label: "Status A→Z" },
  { value: "date_asc", label: "Date ↑ Oldest" },
  { value: "date_desc", label: "Date ↓ Newest" },
];

const getCandidateSlotUrls = (c) =>
  (c.interviewHistories || [])
    .map((h) => ({
      url: h.url,
      roundName: h.roundName,
      historyId: h.interviewHistoryId,
    }))
    .filter((h) => !!h.url);

const scoreCandidate = (c, filters) => {
  let score = 0;
  if (filters.skills) {
    const wanted = filters.skills
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const has = (c.skills || c.candidate?.skills || "").toLowerCase();
    wanted.forEach((s) => {
      if (has.includes(s)) score += 10;
    });
  }
  if (filters.salaryMax) {
    const max = Number(filters.salaryMax);
    const sal = Number(c.expectedSalary || c.candidate?.expectedSalary || 0);
    if (sal && sal <= max) score += 20;
    else if (sal && sal <= max * 1.1) score += 10;
  }
  if (filters.qualificationType) {
    const qt = (
      c.qualificationType ||
      c.candidate?.qualificationType ||
      ""
    ).toUpperCase();
    if (qt === filters.qualificationType.toUpperCase()) score += 15;
  }
  if (filters.qualification) {
    const q = (
      c.qualification ||
      c.candidate?.qualification ||
      ""
    ).toLowerCase();
    if (q === filters.qualification.toLowerCase()) score += 10;
  }
  return score;
};

const Badge = ({ status }) => {
  const s = STATUS_MAP[(status || "").toUpperCase()] || {
    label: status || "—",
    cls: "",
  };
  return <span className={`iv-badge ${s.cls}`}>{s.label}</span>;
};

const RESULT_OPTIONS = [
  {
    value: "SHORTLISTED",
    label: "Shortlisted",
    icon: "⭐",
    cls: "iv-result-opt--shortlist",
  },
  { value: "HOLD", label: "Hold", icon: "⏸", cls: "iv-result-opt--hold" },
  {
    value: "REJECTED",
    label: "Rejected",
    icon: "✕",
    cls: "iv-result-opt--fail",
  },
];

const getColumn = (c, rounds) => {
  if (!c.interviewRoundId) return "__UNDECIDED__";
  const m = rounds.find(
    (r) => String(r.interviewRoundId) === String(c.interviewRoundId),
  );
  return m ? String(m.interviewRoundId) : "__UNDECIDED__";
};

// ─────────────────────────────────────────────────────────────────────────────
//  RoundSlotsPanel — fetches & shows slots for a single round inside timeline
// ─────────────────────────────────────────────────────────────────────────────
const SCHEDULED_BY_LABEL = {
  RECRUITER_HR: "Recruiter / HR",
  CANDIDATE: "Candidate",
  INTERVIEWER: "Interviewer",
  ADMIN: "Admin",
};

const HISTORY_STATUS_LABEL = {
  SCHEDULED: { label: "Scheduled", cls: "tl-slot-meta--scheduled" },
  PROCESSING: { label: "Processing", cls: "tl-slot-meta--processing" },
  SHORTLISTED: { label: "Shortlisted", cls: "tl-slot-meta--pass" },
  SELECTED: { label: "Selected", cls: "tl-slot-meta--pass" },
  HOLD: { label: "On Hold", cls: "tl-slot-meta--hold" },
  REJECTED: { label: "Rejected", cls: "tl-slot-meta--fail" },
  MOVED: { label: "Moved", cls: "tl-slot-meta--scheduled" },
};

const SLOT_STATUS_LABEL = {
  AVAILABLE: { label: "Available", cls: "tl-slot-meta--available" },
  PROCESSED: { label: "Processed", cls: "tl-slot-meta--processed" },
  EXPIRED: { label: "Expired", cls: "tl-slot-meta--fail" },
  CANCELLED: { label: "Cancelled", cls: "tl-slot-meta--hold" },
  BOOKED: { label: "Booked", cls: "tl-slot-meta--scheduled" },
};

const SlotMetaBadge = ({ label, cls }) => (
  <span className={`tl-slot-meta-badge ${cls || ""}`}>{label}</span>
);

const RoundSlotsPanel = ({ historyId, slots: rawSlots = [], onBookSlot }) => {
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const fmtTime = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const handleBook = async () => {
    if (!selectedSlotId) return;
    setBooking(true);
    try {
      const res = await axios.put(RESCHEDULE_API, {
        interviewHistoryId: Number(historyId),
        newSlotId: Number(selectedSlotId),
      });
      const msg =
        (typeof res.data?.data === "string" && res.data.data) ||
        res.data?.message ||
        "Slot booked successfully!";
      setBooked(true);
      toast.success(msg);
      if (onBookSlot) onBookSlot();
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        (typeof err?.response?.data?.data === "string" &&
          err.response.data.data) ||
        "Failed to book slot.";
      toast.error(errMsg);
    } finally {
      setBooking(false);
    }
  };

  if (!rawSlots.length)
    return (
      <div className="tl-slots-panel tl-slots-panel--empty">
        <span>📭</span> No slots for this round.
      </div>
    );

  const bookedSlots = rawSlots.filter(
    (s) => (s.slotStatus || "").toUpperCase() === "BOOKED",
  );
  const availableSlots = rawSlots.filter(
    (s) => (s.slotStatus || "").toUpperCase() === "AVAILABLE",
  );
  const otherSlots = rawSlots.filter((s) => {
    const st = (s.slotStatus || "").toUpperCase();
    return st !== "BOOKED" && st !== "AVAILABLE";
  });

  const renderSlotHistories = (slot) => {
    const histories = slot.slotHistories || [];
    if (!histories.length) return null;
    return (
      <div className="tl-slot-meta-row">
        {histories.map((h) => {
          const histStatusInfo =
            HISTORY_STATUS_LABEL[(h.historyStatus || "").toUpperCase()];
          const scheduledByLabel =
            SCHEDULED_BY_LABEL[(h.scheduledBy || "").toUpperCase()];
          return (
            <React.Fragment key={h.historyId}>
              {histStatusInfo && (
                <SlotMetaBadge
                  label={histStatusInfo.label}
                  cls={histStatusInfo.cls}
                />
              )}
              {scheduledByLabel && (
                <SlotMetaBadge
                  label={`By: ${scheduledByLabel}`}
                  cls="tl-slot-meta--by"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderSlotStatus = (slot) => {
    const st = (slot.slotStatus || "").toUpperCase();
    const info = SLOT_STATUS_LABEL[st];
    if (!info) return null;
    return (
      <div className="tl-slot-meta-row">
        <SlotMetaBadge label={info.label} cls={info.cls} />
      </div>
    );
  };

  return (
    <div className="tl-slots-panel">
      {/* ── Booked slots ── */}
      {bookedSlots.length > 0 && (
        <div className="tl-slots-section">
          <div className="tl-slots-section__title tl-slots-section__title--booked">
            <span className="tl-slot-dot tl-slot-dot--booked" /> Booked Slot
          </div>
          {bookedSlots.map((s) => (
            <div key={s.slotId} className="tl-slot-row tl-slot-row--booked">
              <div className="tl-slot-row__time">
                {fmt(s.startTime)}
                {s.endTime && (
                  <span className="tl-slot-row__until">
                    {" "}
                    → {fmtTime(s.endTime)}
                  </span>
                )}
              </div>
              {renderSlotStatus(s)}
              {renderSlotHistories(s)}
            </div>
          ))}
        </div>
      )}

      {/* ── Available slots ── */}
      {availableSlots.length > 0 && (
        <div className="tl-slots-section">
          <div className="tl-slots-section__title tl-slots-section__title--available">
            <span className="tl-slot-dot tl-slot-dot--available" />
            {availableSlots.length} Available Slot
            {availableSlots.length !== 1 ? "s" : ""}
          </div>
          <div className="tl-slot-grid">
            {availableSlots.map((s) => {
              const isSelected = String(selectedSlotId) === String(s.slotId);
              return (
                <button
                  key={s.slotId}
                  type="button"
                  className={`tl-slot-btn${isSelected ? " tl-slot-btn--selected" : ""}`}
                  onClick={() =>
                    setSelectedSlotId(isSelected ? null : String(s.slotId))
                  }
                  disabled={booked}
                >
                  <div className="tl-slot-btn__time">{fmt(s.startTime)}</div>
                  <div className="tl-slot-btn__end">
                    until {fmtTime(s.endTime)}
                  </div>
                  {renderSlotStatus(s)}
                  {renderSlotHistories(s)}
                  {isSelected && (
                    <div className="tl-slot-btn__check">✓ Selected</div>
                  )}
                </button>
              );
            })}
          </div>
          {selectedSlotId && !booked && (
            <button
              className="tl-slot-book-btn"
              onClick={handleBook}
              disabled={booking}
            >
              {booking ? (
                <>
                  <span className="iv-spin iv-spin--lt" /> Booking…
                </>
              ) : (
                "📅 Confirm Slot Booking →"
              )}
            </button>
          )}
          {booked && (
            <div className="tl-slot-booked-confirm">
              ✅ Slot booked successfully!
            </div>
          )}
        </div>
      )}

      {/* ── Other slots (PROCESSED, CANCELLED, EXPIRED etc.) ── */}
      {otherSlots.length > 0 && (
        <div className="tl-slots-section">
          <div className="tl-slots-section__title tl-slots-section__title--other">
            <span className="tl-slot-dot tl-slot-dot--other" /> Other Slots
          </div>
          {otherSlots.map((s) => (
            <div
              key={s.slotId}
              className="tl-slot-row tl-slot-row--unavailable"
            >
              <div className="tl-slot-row__time">{fmt(s.startTime)}</div>
              {renderSlotStatus(s)}
              {renderSlotHistories(s)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CandidateTimelinePanel
// ─────────────────────────────────────────────────────────────────────────────

const CandidateTimelinePanel = ({ candidate, flows, onClose, onRefresh }) => {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [expandedSlotRound, setExpandedSlotRound] = useState(null);

  if (!candidate) return null;

  const name = getCandidateName(candidate);
  const email = getCandidateEmail(candidate);
  const st = getCandidateStatus(candidate);
  const jobTitle = getJobTitle(candidate);

  const currentFlow = flows.find(
    (f) => String(f.hiringFlowId) === String(candidate.hiringFlowId),
  );

  const historyMap = (candidate.interviewHistories || []).reduce((acc, h) => {
    acc[String(h.roundId)] = h;
    return acc;
  }, {});

  const rawRounds =
    candidate.interviewRounds?.length > 0
      ? getCandidateRounds(candidate)
      : (currentFlow?.rounds || []).map((r, i, arr) => ({
          ...r,
          sequence: r.sequence ?? i + 1,
          initial: r.initial ?? i === 0,
          final: r.final ?? i === arr.length - 1,
        }));

  const flowRounds = rawRounds.slice().sort((a, b) => a.sequence - b.sequence);

  const derivedCurrentRoundId = (() => {
    if (candidate.interviewRoundId) return String(candidate.interviewRoundId);
    const histRoundIds = new Set(
      (candidate.interviewHistories || []).map((h) => String(h.roundId)),
    );
    const lastHistoried = [...flowRounds]
      .reverse()
      .find((r) => histRoundIds.has(String(r.interviewRoundId)));
    return lastHistoried ? String(lastHistoried.interviewRoundId) : null;
  })();

  const getStepState = (round) => {
    const rid = String(round.interviewRoundId);
    const history = historyMap[rid];
    if (history) {
      const hs = (history.status || "").toUpperCase();
      if (hs === "REJECTED") return "rejected";
      if (hs === "SELECTED") return "selected";
      if (hs === "HOLD") return "hold";
      if (hs === "PROCESSING") return "active";
      if (hs === "SHORTLISTED" || hs === "MOVED") return "done";
      if (hs === "SCHEDULED") return "active";
    }
    if (!derivedCurrentRoundId) return "pending";
    const rSeq = round.sequence;
    const curRound = flowRounds.find(
      (r) => String(r.interviewRoundId) === derivedCurrentRoundId,
    );
    const curSeq = curRound?.sequence ?? 0;
    if (rid === derivedCurrentRoundId) return "active";
    if (rSeq < curSeq) return "done";
    return "pending";
  };

  const stepIcon = (state) => {
    if (state === "done")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2.5 7l3.5 3.5 5.5-5.5"
            stroke="#16a34a"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (state === "active") return <span className="tl-step__pulse" />;
    if (state === "rejected")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M3 3l8 8M11 3l-8 8"
            stroke="#dc2626"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    if (state === "selected")
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2.5 7l3.5 3.5 5.5-5.5"
            stroke="#fff"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    if (state === "hold") return <span style={{ fontSize: 11 }}>⏸</span>;
    return null;
  };

  const historyStatusLabel = (status) => {
    const map = {
      SHORTLISTED: { label: "Shortlisted", cls: "tl-hist-badge--pass" },
      SELECTED: { label: "Selected", cls: "tl-hist-badge--pass" },
      PROCESSING: { label: "In Progress", cls: "tl-hist-badge--active" },
      SCHEDULED: { label: "Scheduled", cls: "tl-hist-badge--active" },
      HOLD: { label: "On Hold", cls: "tl-hist-badge--hold" },
      REJECTED: { label: "Rejected", cls: "tl-hist-badge--fail" },
      MOVED: { label: "Moved", cls: "tl-hist-badge--active" },
    };
    const s = map[(status || "").toUpperCase()] || { label: status, cls: "" };
    return <span className={`tl-hist-badge ${s.cls}`}>{s.label}</span>;
  };

  const handleCopy = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : null;

  const fmtTime = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : null;

  return (
    <div className="tl-backdrop" onClick={onClose}>
      <div className="tl-panel" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="tl-panel__hd">
          <div className="tl-panel__candidate">
            <div
              className="iv-avatar iv-avatar--md"
              style={{ background: avatarColor(name) }}
            >
              {getInitials(name)}
            </div>
            <div>
              <div className="tl-panel__name">{name}</div>
              <div className="tl-panel__email">{email || "—"}</div>
              {jobTitle && <div className="tl-panel__job">📋 {jobTitle}</div>}
            </div>
          </div>
          <button className="iv-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Status strip ── */}
        <div className="tl-status-strip">
          <Badge status={st} />
          {candidate.interviewRoundName && (
            <span className="tl-current-round">
              Current round: <strong>{candidate.interviewRoundName}</strong>
            </span>
          )}
          {candidate.interviewDateTime && (
            <span className="tl-date">
              📅 {fmtDate(candidate.interviewDateTime)}
            </span>
          )}
        </div>

        <div className="tl-section-title">
          {currentFlow ? (
            <>
              Hiring Flow: <strong>{currentFlow.hiringFlowName}</strong>
            </>
          ) : (
            "Hiring Progress"
          )}
        </div>

        {/* ── Timeline ── */}
        {flowRounds.length === 0 ? (
          <div className="tl-empty">
            {candidate.hiringFlowId
              ? "No rounds defined in this flow."
              : "No hiring flow assigned yet."}
          </div>
        ) : (
          <div className="tl-timeline">
            {flowRounds.map((round, idx) => {
              const state = getStepState(round);
              const isLast = idx === flowRounds.length - 1;
              const history = historyMap[String(round.interviewRoundId)];
              const hasComment = history?.comment && history.comment.trim();
              const isSlotExpanded =
                expandedSlotRound === String(round.interviewRoundId);
              // Show slot toggle for active/scheduled rounds that have a history
              const canShowSlots = (history?.slots?.length ?? 0) > 0;

              return (
                <div
                  key={round.interviewRoundId}
                  className={`tl-step tl-step--${state}`}
                >
                  {!isLast && (
                    <div
                      className={`tl-step__line tl-step__line--${state === "done" ? "done" : "pending"}`}
                    />
                  )}
                  <div className={`tl-step__circle tl-step__circle--${state}`}>
                    {stepIcon(state)}
                  </div>
                  <div className="tl-step__content">
                    <div className="tl-step__row">
                      <span className="tl-step__name">{round.roundName}</span>
                      <div className="tl-step__tags">
                        {round.initial && (
                          <span className="tl-tag tl-tag--initial">
                            Initial
                          </span>
                        )}
                        {round.final && (
                          <span className="tl-tag tl-tag--final">Final</span>
                        )}
                        {history && historyStatusLabel(history.status)}
                      </div>
                    </div>

                    {history && (
                      <div className="tl-step__details">
                        {history.interviewerName && (
                          <span className="tl-detail">
                            👤 {history.interviewerName}
                          </span>
                        )}
                        {history.roundMode && (
                          <span className="tl-detail">
                            {history.roundMode === "ONLINE"
                              ? "🌐"
                              : history.roundMode === "OFFLINE"
                                ? "📍"
                                : "🔀"}{" "}
                            {history.roundMode}
                          </span>
                        )}
                        {history.interviewDateTime && (
                          <span className="tl-detail">
                            📅 {fmtDate(history.interviewDateTime)}
                          </span>
                        )}
                        {history.interviewEndDateTime && (
                          <span className="tl-detail">
                            🕐 until {fmtTime(history.interviewEndDateTime)}
                          </span>
                        )}
                      </div>
                    )}

                    {history?.url && (
                      <div className="tl-slot-url">
                        <span className="tl-slot-url__label">
                          📎 Slot Booking Link
                        </span>
                        <div className="tl-slot-url__row">
                          <span className="tl-slot-url__val">
                            {history.url}
                          </span>
                          <button
                            className={`tl-slot-url__copy${copiedIdx === history.interviewHistoryId ? " tl-slot-url__copy--done" : ""}`}
                            onClick={() =>
                              handleCopy(
                                history.url,
                                history.interviewHistoryId,
                              )
                            }
                          >
                            {copiedIdx === history.interviewHistoryId
                              ? "✓ Copied"
                              : "Copy"}
                          </button>
                        </div>
                      </div>
                    )}

                    {hasComment && (
                      <div className="tl-comment">
                        <span className="tl-comment__icon">💬</span>
                        <span className="tl-comment__text">
                          {history.comment}
                        </span>
                      </div>
                    )}

                    {/* ── Slot toggle button ── */}
                    {canShowSlots && (
                      <button
                        className={`tl-slot-toggle-btn${isSlotExpanded ? " tl-slot-toggle-btn--open" : ""}`}
                        onClick={() =>
                          setExpandedSlotRound(
                            isSlotExpanded
                              ? null
                              : String(round.interviewRoundId),
                          )
                        }
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="10"
                            height="10"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M3 4.5h6M3 7.5h4"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                          />
                        </svg>
                        {isSlotExpanded ? "Hide Slots" : "View Slots"}
                        <span
                          className={`tl-slot-toggle-btn__arrow${isSlotExpanded ? " tl-slot-toggle-btn__arrow--open" : ""}`}
                        >
                          ▾
                        </span>
                      </button>
                    )}

                    {/* ── Inline slot panel ── */}
                    {isSlotExpanded && (
                      <RoundSlotsPanel
                        historyId={history?.interviewHistoryId}
                        slots={history?.slots || []}
                        onBookSlot={onRefresh}
                      />
                    )}

                    <div className="tl-step__seq">Step {round.sequence}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {candidate.interviewerName && (
          <div className="tl-info-card">
            <div className="tl-info-card__label">Assigned Interviewer</div>
            <div className="tl-info-card__val">{candidate.interviewerName}</div>
          </div>
        )}

        {candidate.sources?.length > 0 && (
          <div className="tl-info-card">
            <div className="tl-info-card__label">Applied Via</div>
            <div className="iv-skills" style={{ marginTop: 4 }}>
              {candidate.sources.map((s) => (
                <span key={s.id} className="iv-skill">
                  {s.sourceName}
                  {s.consultancyName ? ` · ${s.consultancyName}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        <button
          className="iv-ghost-btn"
          style={{ width: "100%", marginTop: "auto" }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  RescheduleModal
// ─────────────────────────────────────────────────────────────────────────────
const RescheduleModal = ({ candidate, onClose, onDone }) => {
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const name = getCandidateName(candidate);
  const histId = getLatestInterviewHistoryId(candidate);

  useEffect(() => {
    if (!histId) {
      setLoadingSlots(false);
      return;
    }
    setLoadingSlots(true);
    axios
      .get(`${AVAILABLE_SLOTS_API}/${histId}/available-slots`)
      .then((res) => {
        setSlots(res.data?.data || []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Failed to load available slots.",
        );
      })
      .finally(() => setLoadingSlots(false));
  }, [histId]);

  const handleSubmit = async () => {
    if (!selectedSlotId) {
      toast.warn("Please select a slot.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.put(RESCHEDULE_API, {
        interviewHistoryId: Number(histId),
        newSlotId: Number(selectedSlotId),
      });
      const msg =
        (typeof res.data?.data === "string" && res.data.data) ||
        res.data?.message ||
        "Interview rescheduled successfully!";
      toast.success(msg);
      setResult({ message: msg }); // store as object so result truthy check works
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        (typeof err?.response?.data?.data === "string" &&
          err.response.data.data) ||
        "Failed to reschedule.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (dateStr) =>
    new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  if (result) {
    return (
      <div className="iv-overlay" onClick={onDone}>
        <div
          className="iv-modal iv-modal--success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="iv-success-ring">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M8 18l7 7 13-13"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="iv-success-title">Rescheduled!</h3>
          <p className="iv-success-sub">
            {name}&apos;s interview has been rescheduled to the new slot.
          </p>
          {/* Show the string message from the API if available */}
          {result.message && (
            <p
              className="iv-result-note"
              style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}
            >
              {result.message}
            </p>
          )}
          <button className="iv-done-btn" onClick={onDone}>
            Done ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="iv-overlay" onClick={onClose}>
      <div
        className="iv-modal iv-modal--reschedule"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="iv-modal__hd">
          <div>
            <h3 className="iv-modal__title">Reschedule Interview</h3>
            <p className="iv-modal__sub">
              Pick a new slot for <strong>{name}</strong>
              {candidate.interviewRoundName && (
                <>
                  {" "}
                  · <em>{candidate.interviewRoundName}</em>
                </>
              )}
            </p>
          </div>
          <button className="iv-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="iv-cand-chip">
          <div
            className="iv-avatar iv-avatar--md"
            style={{ background: avatarColor(name) }}
          >
            {getInitials(name)}
          </div>
          <div>
            <div className="iv-cand-chip__name">{name}</div>
            <div className="iv-cand-chip__meta">
              {getCandidateEmail(candidate)}
              {candidate.interviewRoundName && (
                <>
                  {" "}
                  · Round: <strong>{candidate.interviewRoundName}</strong>
                </>
              )}
              {candidate.interviewerName && (
                <> · 👤 {candidate.interviewerName}</>
              )}
            </div>
          </div>
        </div>

        <div className="iv-field" style={{ marginTop: 16 }}>
          <label className="iv-lbl">
            Available Slots <span className="iv-req">*</span>
          </label>

          {loadingSlots ? (
            <div className="iv-loader" style={{ padding: "24px 0" }}>
              <span className="iv-spin" /> Loading slots...
            </div>
          ) : !histId ? (
            <div className="iv-reschedule-no-slots">
              <span>⚠️</span>
              <p>No interview history found for this candidate.</p>
              <small>Cannot load slots without an interview history ID.</small>
            </div>
          ) : slots.length === 0 ? (
            <div className="iv-reschedule-no-slots">
              <span>📭</span>
              <p>No available slots found for this interview.</p>
              <small>
                Ask the interviewer to open more availability slots.
              </small>
            </div>
          ) : (
            <div className="iv-slot-grid">
              {slots.map((slot) => {
                const isAvailable =
                  slot.slotStatus?.toUpperCase() === "AVAILABLE" &&
                  slot.isScheduled !== true;
                const isSelected = String(selectedSlotId) === String(slot.id);
                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={!isAvailable}
                    className={`iv-slot-card${isSelected ? " iv-slot-card--selected" : ""}${!isAvailable ? " iv-slot-card--unavailable" : ""}`}
                    onClick={() => setSelectedSlotId(String(slot.id))}
                  >
                    <div className="iv-slot-card__time">
                      {fmt(slot.slotStartDateTime)}
                    </div>
                    <div className="iv-slot-card__end">
                      until{" "}
                      {new Date(slot.slotEndDateTime).toLocaleTimeString(
                        "en-IN",
                        { hour: "2-digit", minute: "2-digit", hour12: true },
                      )}
                    </div>
                    {slot.interviewerName && (
                      <div className="iv-slot-card__interviewer">
                        👤 {slot.interviewerName}
                      </div>
                    )}
                    <div
                      className={`iv-slot-card__status iv-slot-card__status--${(slot.slotStatus || "").toLowerCase()}`}
                    >
                      {slot.isScheduled
                        ? "Already Booked"
                        : slot.slotStatus === "AVAILABLE"
                          ? "Available"
                          : slot.slotStatus}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="iv-modal__ft">
          <button className="iv-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="iv-primary-btn iv-primary-btn--reschedule"
            onClick={handleSubmit}
            disabled={submitting || !selectedSlotId || loadingSlots}
          >
            {submitting ? (
              <>
                <span className="iv-spin iv-spin--lt" /> Rescheduling...
              </>
            ) : (
              "Confirm Reschedule →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const CandidateDetailsDrawer = ({ candidate: c, onClose, onViewTimeline }) => {
  if (!c) return null;

  const name = getCandidateName(c);
  const email = getCandidateEmail(c);
  const contact = getCandidateContact(c);
  const st = getCandidateStatus(c);
  const jt = getJobTitle(c);
  const consultancy = getConsultancyName(c);

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : null;

  const fmtDateTime = (d) =>
    d
      ? new Date(d).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : null;

  const resumeUrl = c.candidate?.resumeUrl || c.resumeUrl || "";

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer cdd-drawer">
        {/* ── Header ── */}
        <div className="drawer__header">
          <div className="cdd-header-content">
            <div
              className="iv-avatar iv-avatar--lg"
              style={{ background: avatarColor(name), flexShrink: 0 }}
            >
              {getInitials(name)}
            </div>
            <div>
              <h2 className="drawer__title">{name}</h2>
              <p className="drawer__sub">{email || contact || "—"}</p>
            </div>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="drawer__body cdd-body">
          {/* Status + Job strip */}
          <div className="cdd-strip">
            <Badge status={st} />
            {jt && <span className="cdd-job-chip">📋 {jt}</span>}
            {c.interviewRoundName && (
              <span className="cdd-round-chip">🔄 {c.interviewRoundName}</span>
            )}
          </div>

          {/* ── Section: Contact ── */}
          <div className="cdd-section">
            <div className="cdd-section__title">Contact</div>
            <div className="cdd-grid">
              {email && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Email</span>
                  <a
                    className="cdd-detail__val cdd-detail__val--link"
                    href={`mailto:${email}`}
                  >
                    {email}
                  </a>
                </div>
              )}
              {contact && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Phone</span>
                  <a
                    className="cdd-detail__val cdd-detail__val--link"
                    href={`tel:${contact}`}
                  >
                    {contact}
                  </a>
                </div>
              )}
              {resumeUrl && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Resume</span>
                  <a
                    className="cdd-detail__val cdd-detail__val--link"
                    href={resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📄 View Resume ↗
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* ── Section: Interview Info ── */}
          <div className="cdd-section">
            <div className="cdd-section__title">Interview Info</div>
            <div className="cdd-grid">
              {c.interviewRoundName && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Current Round</span>
                  <span className="cdd-detail__val">
                    {c.interviewRoundName}
                  </span>
                </div>
              )}
              {c.interviewerName && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Interviewer</span>
                  <span className="cdd-detail__val">
                    👤 {c.interviewerName}
                  </span>
                </div>
              )}
              {c.interviewDateTime && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Scheduled At</span>
                  <span className="cdd-detail__val">
                    📅 {fmtDateTime(c.interviewDateTime)}
                  </span>
                </div>
              )}
              {c.expectedClosingDate && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Expected Closing</span>
                  <span className="cdd-detail__val">
                    ⏳ {fmtDate(c.expectedClosingDate)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Section: Candidate Profile ── */}
          {(c.candidate?.experience ||
            c.candidate?.expectedSalary ||
            c.candidate?.qualification ||
            c.candidate?.skills) && (
            <div className="cdd-section">
              <div className="cdd-section__title">Candidate Profile</div>
              <div className="cdd-grid">
                {c.candidate?.experience && (
                  <div className="cdd-detail">
                    <span className="cdd-detail__label">Experience</span>
                    <span className="cdd-detail__val">
                      {c.candidate.experience}
                    </span>
                  </div>
                )}
                {c.candidate?.expectedSalary && (
                  <div className="cdd-detail">
                    <span className="cdd-detail__label">Expected Salary</span>
                    <span className="cdd-detail__val">
                      ₹{c.candidate.expectedSalary.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
                {c.candidate?.qualificationType && (
                  <div className="cdd-detail">
                    <span className="cdd-detail__label">
                      Qualification Type
                    </span>
                    <span className="cdd-detail__val">
                      {c.candidate.qualificationType.replace(/_/g, " ")}
                    </span>
                  </div>
                )}
                {c.candidate?.qualification && (
                  <div className="cdd-detail">
                    <span className="cdd-detail__label">Qualification</span>
                    <span className="cdd-detail__val">
                      {c.candidate.qualification}
                    </span>
                  </div>
                )}
              </div>
              {c.candidate?.skills && (
                <div className="cdd-detail" style={{ marginTop: 8 }}>
                  <span className="cdd-detail__label">Skills</span>
                  <div className="iv-skills" style={{ marginTop: 4 }}>
                    {c.candidate.skills.split(",").map((sk) => (
                      <span key={sk} className="iv-skill">
                        {sk.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Section: Source / Consultancy ── */}
          {(consultancy || c.sources?.length > 0) && (
            <div className="cdd-section">
              <div className="cdd-section__title">Source</div>
              {consultancy && (
                <div className="cdd-detail">
                  <span className="cdd-detail__label">Consultancy</span>
                  <span className="cdd-detail__val">🏢 {consultancy}</span>
                </div>
              )}
              {c.sources?.length > 0 && (
                <div className="iv-skills" style={{ marginTop: 6 }}>
                  {c.sources.map((s) => (
                    <span key={s.id} className="iv-skill">
                      {s.sourceName}
                      {s.consultancyName ? ` · ${s.consultancyName}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Section: Interview History summary ── */}
          {c.interviewHistories?.length > 0 && (
            <div className="cdd-section">
              <div className="cdd-section__title">Round History</div>
              <div className="cdd-history-list">
                {c.interviewHistories.map((h) => {
                  const hs = (h.status || "").toUpperCase();
                  const hsCls =
                    hs === "SELECTED" || hs === "SHORTLISTED"
                      ? "cdd-history-item--pass"
                      : hs === "REJECTED"
                        ? "cdd-history-item--fail"
                        : hs === "HOLD"
                          ? "cdd-history-item--hold"
                          : "cdd-history-item--active";
                  return (
                    <div
                      key={h.interviewHistoryId}
                      className={`cdd-history-item ${hsCls}`}
                    >
                      <div className="cdd-history-item__name">
                        {h.roundName}
                      </div>
                      <div className="cdd-history-item__meta">
                        {h.interviewerName && (
                          <span>👤 {h.interviewerName}</span>
                        )}
                        {h.roundMode && (
                          <span>
                            {h.roundMode === "ONLINE" ? "🌐" : "📍"}{" "}
                            {h.roundMode}
                          </span>
                        )}
                        {h.interviewDateTime && (
                          <span>📅 {fmtDate(h.interviewDateTime)}</span>
                        )}
                      </div>
                      <span
                        className={`tl-hist-badge ${
                          hs === "SELECTED" || hs === "SHORTLISTED"
                            ? "tl-hist-badge--pass"
                            : hs === "REJECTED"
                              ? "tl-hist-badge--fail"
                              : hs === "HOLD"
                                ? "tl-hist-badge--hold"
                                : "tl-hist-badge--active"
                        }`}
                      >
                        {h.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Close
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--primary"
            onClick={() => {
              onClose();
              onViewTimeline && onViewTimeline();
            }}
          >
            📊 View Full Timeline →
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  StatusUpdateModal
// ─────────────────────────────────────────────────────────────────────────────
const StatusUpdateModal = ({ candidate, flows, onClose, onDone }) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [nextRoundId, setNextRoundId] = useState("");
  const [interviewerId, setInterviewerId] = useState("");
  const [roundMode, setRoundMode] = useState("ONLINE");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const name = getCandidateName(candidate);
  const histId = (() => {
    const histories = candidate.interviewHistories;
    if (histories?.length > 0) {
      const sorted = [...histories].sort(
        (a, b) => (b.interviewHistoryId ?? 0) - (a.interviewHistoryId ?? 0),
      );
      return sorted[0].interviewHistoryId;
    }
    return candidate.interviewHistoryId ?? null;
  })();

  const candidateRounds = getCandidateRounds(candidate);

  const currentFlowRound = candidateRounds.find(
    (r) => String(r.interviewRoundId) === String(candidate.interviewRoundId),
  );
  const currentSeq = currentFlowRound?.sequence ?? 0;
  const nextRounds = candidateRounds.filter((r) => r.sequence > currentSeq);
  const isTerminal = ["REJECTED", "HOLD"].includes(selectedStatus);
  const needsNextRound = !isTerminal && selectedStatus && nextRounds.length > 0;
  const noNextRound = !isTerminal && selectedStatus && nextRounds.length === 0;
  const nextRound = nextRounds[0] || null;

  useEffect(() => {
    if (nextRound) {
      setNextRoundId(String(nextRound.interviewRoundId));
      const firstInterviewer = nextRound.interviewers?.[0];
      if (firstInterviewer) {
        setInterviewerId(String(firstInterviewer.id));
      }
      if (nextRound.interviewMode) {
        setRoundMode(nextRound.interviewMode);
      }
    }
  }, [nextRound]);

  const validate = () => {
    const e = {};
    if (!selectedStatus) e.status = "Select a result";
    if (needsNextRound && !nextRound) {
      e.nextRoundId = "No next round available";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        interviewHistoryId: Number(histId),
        status: selectedStatus,
        comments,
        ...(needsNextRound
          ? {
              nextInterviewRoundId: Number(nextRoundId),
              ...(interviewerId ? { interviewer: Number(interviewerId) } : {}),
              roundMode,
            }
          : noNextRound
            ? { nextInterviewRoundId: null }
            : {}),
      };
      const res = await axios.put(STATUS_UPDATE_API, [payload]);
      const resultData = res.data.data || [];

      const hasUrl = resultData.some((r) => r.url);
      if (hasUrl) {
        setResult(resultData);
        toast.success(
          selectedStatus === "REJECTED"
            ? `${name} has been rejected.`
            : selectedStatus === "HOLD"
              ? `${name} has been placed on hold.`
              : `Status updated to ${selectedStatus}`,
        );
      } else {
        toast.success(
          selectedStatus === "REJECTED"
            ? `${name} has been rejected.`
            : selectedStatus === "HOLD"
              ? `${name} has been placed on hold.`
              : `Status updated to ${selectedStatus}`,
        );
        onDone();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  if (result) {
    const hasUrl = result.some((r) => r.url);
    return (
      <div className="iv-overlay" onClick={hasUrl ? undefined : onDone}>
        <div
          className="iv-modal iv-modal--success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="iv-success-ring">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M8 18l7 7 13-13"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="iv-success-title">Status Updated!</h3>
          <p className="iv-success-sub">
            {name}&apos;s interview has been moved to the next round.
          </p>
          {result.map((r, idx) => (
            <div key={idx} className="iv-result-rows">
              {r.roundName && (
                <div className="iv-result-row">
                  <span>Previous Round</span>
                  <strong>{r.roundName}</strong>
                </div>
              )}
              {r.newInterviewRoundName && (
                <div className="iv-result-row">
                  <span>Next Round</span>
                  <strong>{r.newInterviewRoundName}</strong>
                </div>
              )}
              {r.interviewerName && (
                <div className="iv-result-row">
                  <span>Interviewer</span>
                  <strong>{r.interviewerName}</strong>
                </div>
              )}
              {r.status && (
                <div className="iv-result-row">
                  <span>Status</span>
                  <Badge status={r.status} />
                </div>
              )}
              {r.message && (
                <div className="iv-result-row">
                  <span>Note</span>
                  <em className="iv-result-note">{r.message}</em>
                </div>
              )}
              {r.url && (
                <div className="iv-slot-url-block">
                  <div className="iv-slot-url-block__label">
                    📎 Candidate Slot Booking Link
                  </div>
                  <div className="iv-slot-url-block__row">
                    <span className="iv-slot-url-block__url">{r.url}</span>
                    <button
                      className={`iv-slot-url-block__copy${copiedIdx === idx ? " iv-slot-url-block__copy--done" : ""}`}
                      onClick={() => handleCopy(r.url, idx)}
                    >
                      {copiedIdx === idx ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="iv-slot-url-block__hint">
                    Share this link with the candidate so they can pick an
                    available interview slot.
                  </p>
                </div>
              )}
            </div>
          ))}
          <button className="iv-done-btn" onClick={onDone}>
            Done ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="iv-overlay" onClick={onClose}>
      <div
        className="iv-modal iv-modal--status-update"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="iv-modal__hd">
          <div>
            <h3 className="iv-modal__title">Update Round Result</h3>
            <p className="iv-modal__sub">
              <strong>{name}</strong>
              {candidate.interviewRoundName && (
                <>
                  {" "}
                  · <em>{candidate.interviewRoundName}</em>
                </>
              )}
            </p>
          </div>
          <button className="iv-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="iv-cand-chip">
          <div
            className="iv-avatar iv-avatar--md"
            style={{ background: avatarColor(name) }}
          >
            {getInitials(name)}
          </div>
          <div>
            <div className="iv-cand-chip__name">{name}</div>
            <div className="iv-cand-chip__meta">
              {candidate.interviewRoundName && (
                <>
                  Round: <strong>{candidate.interviewRoundName}</strong>
                  {currentFlowRound && candidateRounds.length > 0 && (
                    <>
                      {" "}
                      ({currentFlowRound.sequence} of {candidateRounds.length})
                    </>
                  )}
                </>
              )}
              {candidate.interviewerName && <> · {candidate.interviewerName}</>}
            </div>
          </div>
        </div>

        <div className="iv-field">
          <label className="iv-lbl">
            Round Result <span className="iv-req">*</span>
          </label>
          <div className="iv-result-grid">
            {RESULT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`iv-result-opt ${opt.cls}${selectedStatus === opt.value ? " iv-result-opt--selected" : ""}`}
                onClick={() => {
                  setSelectedStatus(opt.value);
                  setErrors({});
                }}
              >
                <span className="iv-result-opt__icon">{opt.icon}</span>
                <span className="iv-result-opt__label">{opt.label}</span>
              </button>
            ))}
          </div>
          {errors.status && <span className="iv-err">{errors.status}</span>}
        </div>

        {selectedStatus && (
          <div
            className={`iv-status-hint iv-status-hint--${selectedStatus.toLowerCase()}`}
          >
            {selectedStatus === "REJECTED" &&
              "⚠️ This candidate will be marked as rejected and removed from the pipeline."}
            {selectedStatus === "HOLD" &&
              "⏸ This candidate will be placed on hold."}
            {noNextRound &&
              "ℹ️ No further rounds in this flow — the result will be submitted as-is."}
            {needsNextRound &&
              `📋 Schedule the next round: ${nextRounds[0]?.roundName}${nextRounds.length > 1 ? ` (+${nextRounds.length - 1} more)` : ""}`}
          </div>
        )}

        {needsNextRound && (
          <div className="iv-next-round-block">
            <div className="iv-next-round-block__title">
              <span className="iv-next-round-block__dot" />
              Schedule Next Round
            </div>

            <div className="iv-field">
              <label className="iv-lbl">Next Round</label>
              <div className="iv-readonly">
                {nextRound ? nextRound.roundName : "—"}
              </div>
            </div>
            <div className="iv-field">
              <label className="iv-lbl">Interview Mode</label>
              <div className="iv-readonly">{roundMode}</div>
            </div>

            <div className="iv-two-col">
              <div className="iv-field">
                <label className="iv-lbl">Interviewer</label>
                <div className="iv-readonly">
                  {nextRound?.interviewers?.length > 0
                    ? nextRound.interviewers.map((iv) => iv.name).join(", ")
                    : "Not assigned"}
                </div>
              </div>
            </div>

            <div className="iv-field">
              <label className="iv-lbl">Comments / Instructions</label>
              <textarea
                className="iv-ta"
                rows={2}
                placeholder="Instructions for next round interviewer..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
        )}

        {(isTerminal || noNextRound) && (
          <div className="iv-field">
            <label className="iv-lbl">
              {selectedStatus === "REJECTED"
                ? "Reason for rejection"
                : selectedStatus === "HOLD"
                  ? "Reason for hold"
                  : "Remarks"}
            </label>{" "}
            <textarea
              className="iv-ta"
              rows={2}
              placeholder={
                selectedStatus === "REJECTED"
                  ? "Reason for rejection..."
                  : "Reason for hold..."
              }
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        )}

        <div className="iv-modal__ft">
          <button className="iv-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className={`iv-primary-btn${["REJECTED", "HOLD"].includes(selectedStatus) ? " iv-primary-btn--danger" : ""}`}
            onClick={handleSubmit}
            disabled={submitting || !selectedStatus}
          >
            {submitting ? (
              <>
                <span className="iv-spin iv-spin--lt" /> Saving...
              </>
            ) : selectedStatus === "REJECTED" ? (
              "Confirm Rejection →"
            ) : selectedStatus === "HOLD" ? (
              "Confirm Hold →"
            ) : needsNextRound ? (
              "Save & Schedule Next Round →"
            ) : (
              "Save Result →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── KanbanCard ───────────────────────────────────────────────────────────────
const KanbanCard = ({
  candidate: c,
  onSchedule,
  onStatusUpdate,
  onReschedule,
  onDragStart,
  onViewTimeline,
  onViewDetails,
}) => {
  const name = getCandidateName(c);
  const email = getCandidateEmail(c);
  const st = getCandidateStatus(c);
  const isApplied =
    (st === "APPLIED" || st === "NOT_SCHEDULED") &&
    c.scheduledInterview === false;
  const canReschedule = st === "SCHEDULED";
  const canSchedule = st === "PROCESSING";

  return (
    <div className="kb-card" draggable onDragStart={(e) => onDragStart(e, c)}>
      <div className="kb-card__drag">⠿</div>
      <div className="kb-card__top">
        <div
          className="iv-avatar iv-avatar--sm"
          style={{ background: avatarColor(name) }}
        >
          {getInitials(name)}
        </div>
        <div className="kb-card__info">
          <div className="kb-card__name">{name}</div>
          <div className="kb-card__sub">{c.interviewRoundName || "—"}</div>
        </div>
        <Badge status={st} />
      </div>

      <div className="kb-card__meta">
        {email && (
          <span>
            ✉{" "}
            <a
              href={`mailto:${email}`}
              className="kb-card__email-link"
              onClick={(e) => e.stopPropagation()}
              title={email}
            >
              {email.length > 24 ? email.slice(0, 24) + "…" : email}
            </a>
          </span>
        )}
        {getCandidateContact(c) && <span>📞 {getCandidateContact(c)}</span>}
        {c.interviewDateTime && (
          <span>
            📅{" "}
            {new Date(c.interviewDateTime).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            })}
          </span>
        )}
        {c.interviewerName && <span>👤 {c.interviewerName}</span>}
        {c.sources?.length > 0 && (
          <span>🔗 {c.sources.map((s) => s.sourceName).join(", ")}</span>
        )}
      </div>

      <div className="kb-card__actions">
        <button
          className="iv-action-btn iv-action-btn--details kb-card__btn"
          onClick={() => onViewDetails(c)}
        >
          📋 Details
        </button>
        <button
          className="iv-action-btn iv-action-btn--timeline kb-card__btn"
          onClick={() => onViewTimeline(c)}
        >
          📊 Timeline
        </button>

        {isApplied && (
          <button
            className="iv-action-btn iv-action-btn--schedule kb-card__btn"
            onClick={() => onSchedule(c)}
          >
            📅 Move to Interview
          </button>
        )}

        {["SCHEDULED", "HOLD"].includes(st) && (
          <button
            className="iv-action-btn iv-action-btn--update-status kb-card__btn"
            onClick={() => onStatusUpdate(c)}
          >
            ✎ Update
          </button>
        )}

        {canReschedule && (
          <button
            className="iv-action-btn iv-action-btn--reschedule kb-card__btn"
            onClick={() => onReschedule(c)}
          >
            🔄 Reschedule Slot
          </button>
        )}

        {canSchedule && (
          <button
            className="iv-action-btn iv-action-btn--reschedule kb-card__btn"
            onClick={() => onReschedule(c)}
          >
            📅 Schedule Slot
          </button>
        )}
      </div>
    </div>
  );
};

// ─── KanbanBoard ──────────────────────────────────────────────────────────────
const KanbanBoard = ({
  candidates,
  onSchedule,
  onStatusUpdate,
  onReschedule,
  flows,
  onMove,
  onViewTimeline,
  onViewDetails,
  jobOptions,
  jobCounts,
}) => {
  const [jobFilter, setJobFilter] = useState(
    jobOptions.length > 0 ? jobOptions[0].id : "ALL",
  );
  const [dragOver, setDragOver] = useState(null);
  const dragRef = useRef(null);

  const filteredCandidates = candidates.filter(
    (c) => jobFilter === "ALL" || String(getJobPostId(c) || "") === jobFilter,
  );

  const selectedFlow = flows.find((f) =>
    (f.jobPosts || []).some((jp) => String(jp.jobPostId) === String(jobFilter)),
  );

  const rounds = selectedFlow
    ? (selectedFlow.rounds || [])
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
    : Array.from(
        new Map(
          filteredCandidates
            .flatMap((c) => getCandidateRounds(c))
            .map((r) => [r.interviewRoundId, r]),
        ).values(),
      ).sort((a, b) => a.sequence - b.sequence);

  const cols = [
    { id: "__UNDECIDED__", label: "Yet to be Decided", undecided: true },
    ...rounds.map((r) => ({
      id: String(r.interviewRoundId),
      label: r.roundName,
      round: r,
      initial: r.initial,
      final: r.final,
    })),
  ];

  const buckets = cols.reduce((acc, col) => {
    if (col.undecided) {
      acc[col.id] = filteredCandidates.filter((c) => {
        const st = getCandidateStatus(c);
        return st === "APPLIED" || st === "NOT_SCHEDULED";
      });
    } else {
      acc[col.id] = filteredCandidates.filter(
        (c) => getColumn(c, rounds) === col.id,
      );
    }
    return acc;
  }, {});

  const onDragStart = (e, c) => {
    dragRef.current = c;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    setDragOver(id);
  };
  const onDrop = (e, col) => {
    e.preventDefault();
    setDragOver(null);
    const c = dragRef.current;
    dragRef.current = null;
    if (!c || getColumn(c, rounds) === col.id) return;
    onMove(c, col.undecided ? null : col.round);
  };

  return (
    <div className="kb-root">
      {jobOptions.length > 0 && (
        <div className="iv-flow-filter-bar" style={{ marginBottom: 16 }}>
          <span className="iv-flow-filter-bar__label">Job</span>
          {jobOptions.map((j) => (
            <button
              key={j.id}
              className={`iv-flow-chip iv-flow-chip--job${jobFilter === j.id ? " iv-flow-chip--active" : ""}`}
              onClick={() => setJobFilter(j.id)}
            >
              {j.title}
              <span className="iv-filter-tab__n">{jobCounts[j.id] || 0}</span>
            </button>
          ))}
        </div>
      )}

      <div className="kb-board">
        {cols.map((col) => {
          const cards = buckets[col.id] || [];
          const isOver = dragOver === col.id;
          return (
            <div
              key={col.id}
              className={`kb-col${col.undecided ? " kb-col--undecided" : ""}${isOver ? " kb-col--over" : ""}`}
              onDragOver={(e) => onDragOver(e, col.id)}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, col)}
            >
              <div className="kb-col__head">
                <div className="kb-col__title">
                  {col.undecided && <span className="kb-col__dot" />}
                  {col.initial && (
                    <span className="kb-col__tag kb-col__tag--initial">
                      Initial
                    </span>
                  )}
                  {col.final && (
                    <span className="kb-col__tag kb-col__tag--final">
                      Final
                    </span>
                  )}
                  {col.label}
                </div>
                <span className="kb-col__count">{cards.length}</span>
              </div>
              <div className="kb-col__body">
                {cards.length === 0 ? (
                  <div className="kb-col__empty">
                    {isOver ? "Drop here ↓" : "No candidates"}
                  </div>
                ) : (
                  cards.map((c) => (
                    <KanbanCard
                      key={getCandidateId(c)}
                      candidate={c}
                      onSchedule={onSchedule}
                      onStatusUpdate={onStatusUpdate}
                      onReschedule={onReschedule}
                      onDragStart={onDragStart}
                      onViewTimeline={onViewTimeline}
                      onViewDetails={onViewDetails}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  BulkScheduleModal
// ─────────────────────────────────────────────────────────────────────────────
const BulkScheduleModal = ({ candidates, onClose, onDone }) => {
  const [sourceMaps, setSourceMaps] = useState(() => {
    const map = {};
    candidates.forEach((c) => {
      const id = String(getCandidateId(c));
      const sources = c.sources || [];
      map[id] = sources.length > 0 ? String(sources[0].id) : "";
    });
    return map;
  });
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const needsSourceSelection = candidates.filter(
    (c) => (c.sources || []).length > 1,
  );

  const setSourceFor = (candidateId, sourceId) => {
    setSourceMaps((prev) => ({ ...prev, [candidateId]: sourceId }));
  };

  const validate = () => {
    for (const c of candidates) {
      const id = String(getCandidateId(c));
      if ((c.sources || []).length > 0 && !sourceMaps[id]) {
        toast.warn(`Please select a source for ${getCandidateName(c)}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const payloads = candidates.map((c) => ({
      appliedCandidateHistoryId: Number(getCandidateId(c)),
      appliedCandidateHistorySourceId: Number(
        sourceMaps[String(getCandidateId(c))],
      ),
      hiringFlowId: c.hiringFlowId,
      comments,
      status: "MOVED",
    }));

    try {
      const res = await axios.put(MOVE_API, payloads);
      const data = res.data.data || [];
      setResults(data);
      toast.success(`${candidates.length} candidates scheduled successfully!`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Bulk schedule failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  if (results) {
    const hasUrls = results.some((r) => r.url);
    return (
      <div className="iv-overlay" onClick={hasUrls ? undefined : onDone}>
        <div
          className="iv-modal iv-modal--success iv-modal--bulk-success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="iv-success-ring">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M8 18l7 7 13-13"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="iv-success-title">
            {candidates.length} Interviews Scheduled!
          </h3>
          <p className="iv-success-sub">
            All selected candidates have been moved to interview.
          </p>

          <div className="iv-bulk-results">
            {results.map((r, idx) => (
              <div key={idx} className="iv-bulk-result-item">
                <div className="iv-bulk-result-item__name">
                  {r.candidateName || `Candidate ${idx + 1}`}
                </div>
                <div className="iv-bulk-result-item__meta">
                  {r.interviewRoundName && (
                    <span>📋 {r.interviewRoundName}</span>
                  )}
                  {r.interviewerName && <span>👤 {r.interviewerName}</span>}
                  {r.status && <Badge status={r.status} />}
                </div>
                {r.url && (
                  <div className="iv-slot-url-block" style={{ marginTop: 6 }}>
                    <div className="iv-slot-url-block__label">
                      📎 Slot Booking Link
                    </div>
                    <div className="iv-slot-url-block__row">
                      <span className="iv-slot-url-block__url">{r.url}</span>
                      <button
                        className={`iv-slot-url-block__copy${copiedIdx === idx ? " iv-slot-url-block__copy--done" : ""}`}
                        onClick={() => handleCopy(r.url, idx)}
                      >
                        {copiedIdx === idx ? "✓ Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className="iv-done-btn" onClick={onDone}>
            Done ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="iv-overlay" onClick={onClose}>
      <div
        className="iv-modal iv-modal--bulk-schedule"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="iv-modal__hd">
          <div>
            <h3 className="iv-modal__title">Bulk Schedule Interviews</h3>
            <p className="iv-modal__sub">
              Schedule{" "}
              <strong>
                {candidates.length} candidate
                {candidates.length !== 1 ? "s" : ""}
              </strong>{" "}
              to move into the interview pipeline.
            </p>
          </div>
          <button className="iv-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="iv-bulk-list">
          <div className="iv-bulk-list__header">
            <span>Candidates to schedule</span>
            <span className="iv-bulk-list__count">{candidates.length}</span>
          </div>
          <div className="iv-bulk-list__body">
            {candidates.map((c) => {
              const name = getCandidateName(c);
              const email = getCandidateEmail(c);
              const id = String(getCandidateId(c));
              const sources = c.sources || [];
              const needsSource = sources.length > 1;

              return (
                <div key={id} className="iv-bulk-cand-row">
                  <div
                    className="iv-avatar iv-avatar--sm"
                    style={{ background: avatarColor(name), flexShrink: 0 }}
                  >
                    {getInitials(name)}
                  </div>
                  <div className="iv-bulk-cand-row__info">
                    <div className="iv-bulk-cand-row__name">{name}</div>
                    <div className="iv-bulk-cand-row__email">
                      {email || getJobTitle(c) || "—"}
                    </div>
                  </div>
                  {needsSource ? (
                    <div className="iv-bulk-cand-row__source">
                      <select
                        className="iv-sel iv-sel--compact"
                        value={sourceMaps[id] || ""}
                        onChange={(e) => setSourceFor(id, e.target.value)}
                      >
                        <option value="">Select source</option>
                        {sources.map((s) => (
                          <option
                            key={s.id || s.sourceId}
                            value={s.id || s.sourceId}
                          >
                            {s.sourceName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : sources.length === 1 ? (
                    <span className="iv-skill" style={{ flexShrink: 0 }}>
                      {sources[0].sourceName}
                    </span>
                  ) : (
                    <span className="iv-nil" style={{ flexShrink: 0 }}>
                      No source
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="iv-field" style={{ marginTop: 12 }}>
          <label className="iv-lbl">
            Comments / Instructions{" "}
            <span style={{ fontWeight: 400, color: "#9ca3af" }}>
              (applied to all)
            </span>
          </label>
          <textarea
            className="iv-ta"
            rows={3}
            placeholder="Topics to assess, special instructions for all interviewers..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        {needsSourceSelection.length > 0 && (
          <div className="iv-status-hint iv-status-hint--info">
            ℹ️ {needsSourceSelection.length} candidate
            {needsSourceSelection.length !== 1 ? "s have" : " has"} multiple
            sources — please select one for each.
          </div>
        )}

        <div className="iv-modal__ft">
          <button className="iv-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="iv-primary-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="iv-spin iv-spin--lt" /> Scheduling{" "}
                {candidates.length}...
              </>
            ) : (
              <>
                📅 Schedule {candidates.length} Candidate
                {candidates.length !== 1 ? "s" : ""} →
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  BulkActionBar
// ─────────────────────────────────────────────────────────────────────────────
const BulkActionBar = ({ selectedCount, onSchedule, onClear }) => {
  if (selectedCount === 0) return null;
  return (
    <div className="iv-bulk-bar">
      <div className="iv-bulk-bar__left">
        <div className="iv-bulk-bar__check-icon">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 7l4 4 6-6"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span className="iv-bulk-bar__count">
          {selectedCount} candidate{selectedCount !== 1 ? "s" : ""} selected
        </span>
      </div>
      <div className="iv-bulk-bar__actions">
        <button
          className="iv-bulk-bar__btn iv-bulk-bar__btn--primary"
          onClick={onSchedule}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <rect
              x="1"
              y="2"
              width="11"
              height="10"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <path
              d="M4 1v2M9 1v2M1 5h11"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <path
              d="M6.5 7v2M5.5 8h2"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          Schedule All
        </button>
        <button
          className="iv-bulk-bar__btn iv-bulk-bar__btn--ghost"
          onClick={onClear}
        >
          ✕ Clear
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ConsultancyGroupRow
// ─────────────────────────────────────────────────────────────────────────────
const ConsultancyGroupRow = ({
  label,
  consultancyId,
  count,
  isOpen,
  onToggle,
  color,
}) => (
  <tr
    className="iv-group-row"
    onClick={onToggle}
    style={{ "--group-color": color }}
  >
    <td colSpan={9}>
      <div className="iv-group-row__inner">
        <span className="iv-group-row__chevron">{isOpen ? "▾" : "▸"}</span>
        <span className="iv-group-row__icon">🏢</span>
        <span className="iv-group-row__name">{label}</span>
        {consultancyId && (
          <span className="iv-group-row__id">ID: {consultancyId}</span>
        )}
        <span className="iv-group-row__count">
          {count} candidate{count !== 1 ? "s" : ""}
        </span>
      </div>
    </td>
  </tr>
);

// ─── TablePagination ──────────────────────────────────────────────────────────
const TablePagination = ({ page, total, pageSize, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }
  return (
    <div className="jp-pagination">
      <span className="jp-pagination__info">
        {from}–{to} of {total}
      </span>
      <div className="jp-pagination__btns">
        <button
          className="jp-pagination__btn"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
        >
          ‹ Prev
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e-${i}`} className="jp-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`jp-pagination__btn${p === page ? " jp-pagination__btn--active" : ""}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className="jp-pagination__btn"
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  InterviewPipeline
// ─────────────────────────────────────────────────────────────────────────────
const InterviewPipeline = () => {
  const [candidates, setCandidates] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("default");
  const [jobFilter, setJobFilter] = useState("ALL");
  const [consultancyFilter, setConsultancyFilter] = useState("ALL");
  const [groupByConsultancy, setGroupByConsultancy] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState("table");
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [statusUpdateTarget, setStatusUpdateTarget] = useState(null);
  const [timelineCandidate, setTimelineCandidate] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const [detailsCandidate, setDetailsCandidate] = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 50;
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [filters, setFilters] = useState({
    skills: "",
    salaryMax: "",
    qualificationType: "",
    qualification: "",
  });
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios
        .get(CANDIDATE_API)
        .then((r) => r.data.data || [])
        .catch(() => []),
      axios
        .get(HIRING_FLOW_API)
        .then((r) => r.data.data || [])
        .catch(() => []),
    ])
      .then(([cands, hf]) => {
        setCandidates(cands);
        setFlows(hf);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTablePage(1);
  }, [statusFilter, jobFilter, consultancyFilter, search, filters]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const done = () => {
    setScheduleTarget(null);
    setStatusUpdateTarget(null);
    setSelectedIds(new Set());
    setBulkScheduleOpen(false);
    fetchAll();
  };

  const handleKanbanMove = (candidate, round) => {
    const st = getCandidateStatus(candidate);
    if (st === "SCHEDULED") {
      setStatusUpdateTarget({ ...candidate, _preselectRound: round || null });
    }

    if (st === "APPLIED" || st === "NOT_SCHEDULED") {
      setScheduleTarget({ ...candidate, _preselectRound: round || null });
    } else if (st === "SCHEDULED" || st === "HOLD") {
      setStatusUpdateTarget(candidate);
    } else if (st === "PROCESSING") {
      setRescheduleTarget(candidate);
    } else {
      // fallback — still open schedule modal
      setScheduleTarget({ ...candidate, _preselectRound: round || null });
    }
  };

  const jobOptions = (() => {
    const map = new Map();
    candidates.forEach((c) => {
      const jid = getJobPostId(c);
      const title = getJobTitle(c);
      if (jid && title && !map.has(String(jid))) {
        map.set(String(jid), title);
      }
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  })();

  const jobCounts = candidates.reduce((acc, c) => {
    const jid = String(getJobPostId(c) || "");
    if (jid) acc[jid] = (acc[jid] || 0) + 1;
    return acc;
  }, {});

  const consultancyOptions = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const name = getConsultancyName(c);
      const id = getConsultancyId(c);
      if (name) {
        const key = id ? String(id) : `name:${name}`;
        if (!map.has(key)) map.set(key, { key, name, id });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [candidates]);

  const consultancyCounts = useMemo(() => {
    const acc = {};
    candidates.forEach((c) => {
      const name = getConsultancyName(c);
      const id = getConsultancyId(c);
      if (name) {
        const key = id ? String(id) : `name:${name}`;
        acc[key] = (acc[key] || 0) + 1;
      }
    });
    return acc;
  }, [candidates]);

  // ── Status counts for filter tabs ─────────────────────────────────────────
  const statusCounts = useMemo(() => {
    const acc = { ALL: candidates.length };
    candidates.forEach((c) => {
      const st = getCandidateStatus(c);
      acc[st] = (acc[st] || 0) + 1;
    });
    return acc;
  }, [candidates]);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const visible = useMemo(() => {
    let result = candidates.filter((c) => {
      const q = search.toLowerCase();
      const name = getCandidateName(c);
      const email = getCandidateEmail(c);
      const matchQ =
        !q ||
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        (c.interviewRoundName || "").toLowerCase().includes(q) ||
        getJobTitle(c).toLowerCase().includes(q) ||
        (getConsultancyName(c) || "").toLowerCase().includes(q) ||
        (c.sources || []).some((s) => s.sourceName?.toLowerCase().includes(q));

      const st = getCandidateStatus(c);
      const matchStatus = statusFilter === "ALL" || st === statusFilter;
      const matchJob =
        jobFilter === "ALL" || String(getJobPostId(c) || "") === jobFilter;

      const matchConsultancy = (() => {
        if (consultancyFilter === "ALL") return true;
        if (consultancyFilter === UNGROUPED_KEY) return !getConsultancyName(c);
        const cName = getConsultancyName(c);
        const cId = getConsultancyId(c);
        const key = cId ? String(cId) : cName ? `name:${cName}` : null;
        return key === consultancyFilter;
      })();

      const matchSkills = (() => {
        if (!filters.skills) return true;
        const wanted = filters.skills
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        const has = (c.skills || c.candidate?.skills || "").toLowerCase();
        return wanted.some((s) => has.includes(s));
      })();

      const matchSalary = (() => {
        if (!filters.salaryMax) return true;
        const sal = Number(
          c.expectedSalary || c.candidate?.expectedSalary || 0,
        );
        if (!sal) return true;
        return sal <= Number(filters.salaryMax) * 1.1;
      })();

      const matchQualType =
        !filters.qualificationType ||
        (
          c.qualificationType ||
          c.candidate?.qualificationType ||
          ""
        ).toUpperCase() === filters.qualificationType.toUpperCase();

      const matchQual =
        !filters.qualification ||
        (c.qualification || c.candidate?.qualification || "").toLowerCase() ===
          filters.qualification.toLowerCase();

      return (
        matchQ &&
        matchStatus &&
        matchJob &&
        matchConsultancy &&
        matchSkills &&
        matchSalary &&
        matchQualType &&
        matchQual
      );
    });

    if (hasActiveFilters) {
      result = result
        .map((c) => ({ ...c, _score: scoreCandidate(c, filters) }))
        .sort((a, b) => b._score - a._score);
    }

    // ── Sort ────────────────────────────────────────────────────────────────
    if (!hasActiveFilters || sortBy !== "default") {
      result = [...result].sort((a, b) => {
        switch (sortBy) {
          case "name_asc":
            return getCandidateName(a).localeCompare(getCandidateName(b));
          case "name_desc":
            return getCandidateName(b).localeCompare(getCandidateName(a));
          case "status_asc":
            return getCandidateStatus(a).localeCompare(getCandidateStatus(b));
          case "date_asc": {
            const da = a.interviewDateTime
              ? new Date(a.interviewDateTime).getTime()
              : Infinity;
            const db = b.interviewDateTime
              ? new Date(b.interviewDateTime).getTime()
              : Infinity;
            return da - db;
          }
          case "date_desc": {
            const da = a.interviewDateTime
              ? new Date(a.interviewDateTime).getTime()
              : -Infinity;
            const db = b.interviewDateTime
              ? new Date(b.interviewDateTime).getTime()
              : -Infinity;
            return db - da;
          }
          default:
            return 0;
        }
      });
    }

    return result;
  }, [
    candidates,
    search,
    statusFilter,
    sortBy,
    jobFilter,
    consultancyFilter,
    filters,
    hasActiveFilters,
  ]);

  const pipelineStats = useMemo(() => {
    const uniqueCandidates = new Set(
      candidates
        .map((candidate) => String(getCandidateId(candidate)))
        .filter(Boolean),
    ).size;

    const scheduledCount = candidates.filter((candidate) => {
      const status = getCandidateStatus(candidate);
      return (
        candidate.scheduledInterview === true ||
        !!candidate.interviewHistoryId ||
        (candidate.interviewHistories?.length ?? 0) > 0 ||
        status === "SCHEDULED" ||
        status === "MOVED"
      );
    }).length;

    const actionPendingCount = candidates.filter((candidate) =>
      ["APPLIED", "NOT_SCHEDULED", "HOLD"].includes(
        getCandidateStatus(candidate),
      ),
    ).length;

    return [
      {
        label: "Pipeline Records",
        value: visible.length,
        tone: "blue",
        sub: `${candidates.length} total records loaded`,
      },
      {
        label: "Unique Candidates",
        value: uniqueCandidates,
        tone: "green",
        sub: "Distinct people in process",
      },
      {
        label: "Scheduled",
        value: scheduledCount,
        tone: "violet",
        sub: "Interview slot already assigned",
      },
      {
        label: "Needs Action",
        value: actionPendingCount,
        tone: "amber",
        sub: "Applied, unscheduled, or on hold",
      },
    ];
  }, [candidates, visible.length]);

  const groupedRows = useMemo(() => {
    if (!groupByConsultancy) return null;

    const groups = new Map();

    visible.forEach((c) => {
      const cName = getConsultancyName(c);
      const cId = getConsultancyId(c);
      const key = cId ? String(cId) : cName ? `name:${cName}` : UNGROUPED_KEY;
      const label = cName || "Direct / No Consultancy";
      if (!groups.has(key)) {
        groups.set(key, { key, label, consultancyId: cId, candidates: [] });
      }
      groups.get(key).candidates.push(c);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.key === UNGROUPED_KEY) return 1;
      if (b.key === UNGROUPED_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [visible, groupByConsultancy]);

  const schedulableVisible = visible.filter((c) => {
    const st = getCandidateStatus(c);
    return (
      (st === "APPLIED" || st === "NOT_SCHEDULED") &&
      c.scheduledInterview === false
    );
  });

  const allSchedulableSelected =
    schedulableVisible.length > 0 &&
    schedulableVisible.every((c) => selectedIds.has(String(getCandidateId(c))));

  const toggleSelectAll = () => {
    if (allSchedulableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(
        new Set(schedulableVisible.map((c) => String(getCandidateId(c)))),
      );
    }
  };

  const toggleSelect = (c) => {
    const id = String(getCandidateId(c));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedCandidates = candidates.filter((c) =>
    selectedIds.has(String(getCandidateId(c))),
  );

  const hasActiveFilter =
    statusFilter !== "ALL" ||
    jobFilter !== "ALL" ||
    consultancyFilter !== "ALL";

  const groupColor = (key) =>
    COLORS[
      Array.from(key || "").reduce((s, ch) => s + ch.charCodeAt(0), 0) %
        COLORS.length
    ];

  const renderCandidateRow = (c, i) => {
    const id = getCandidateId(c);
    const sid = String(id);
    const name = getCandidateName(c);
    const email = getCandidateEmail(c);
    const st = getCandidateStatus(c);
    const jt = getJobTitle(c);
    const consultancyName = getConsultancyName(c);
    const isApplied =
      (st === "APPLIED" || st === "NOT_SCHEDULED") &&
      c.scheduledInterview === false;

    const isScheduled =
      !!c.interviewHistoryId ||
      (c.interviewHistories?.length ?? 0) > 0 ||
      c.scheduledInterview === true ||
      st === "MOVED";
    const canReschedule = st === "SCHEDULED";
    const canSchedule = st === "PROCESSING";
    const isSelected = selectedIds.has(sid);

    return (
      <tr
        key={id || i}
        className={`${isScheduled ? "iv-tr--scheduled" : ""} iv-tr--clickable${isSelected ? " iv-tr--selected" : ""}`}
        onClick={() => setTimelineCandidate(c)}
      >
        <td onClick={(e) => e.stopPropagation()}>
          {isApplied && (
            <input
              type="checkbox"
              className="iv-checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(c)}
            />
          )}
        </td>

        <td className="iv-td-num">{i + 1}</td>

        <td>
          <div className="iv-person">
            <div
              className="iv-avatar"
              style={{ background: avatarColor(name) }}
            >
              {getInitials(name)}
            </div>
            <div>
              <div className="iv-person__name">{name}</div>
              <div className="iv-person__sub">{email || "—"}</div>
              {hasActiveFilters && c._score > 0 && (
                <div className="cand-score-badge">★ {c._score} pts</div>
              )}
            </div>
          </div>
        </td>

        <td>
          {jt ? (
            <span className="iv-job-chip">{jt}</span>
          ) : (
            <span className="iv-nil">—</span>
          )}
        </td>

        <td>
          <div className="iv-sources-cell">
            {consultancyName && (
              <span className="iv-consultancy-badge">🏢 {consultancyName}</span>
            )}
            {c.sources?.length > 0 ? (
              <div className="iv-skills">
                {c.sources
                  .filter((s) => s.sourceType !== "CONSULTANCY")
                  .map((s) => (
                    <span key={s.id} className="iv-skill">
                      {s.sourceName}
                    </span>
                  ))}
                {!consultancyName &&
                  c.sources
                    .filter((s) => s.sourceType === "CONSULTANCY")
                    .map((s) => (
                      <span key={s.id} className="iv-consultancy-badge">
                        🏢 {s.sourceName}
                      </span>
                    ))}
              </div>
            ) : !consultancyName ? (
              <span className="iv-nil">—</span>
            ) : null}
          </div>
        </td>

        <td>
          <Badge status={st} />
        </td>

        <td>
          {c.interviewRoundName ? (
            <div>
              <div className="iv-round__name">{c.interviewRoundName}</div>
              {c.interviewerName && (
                <div className="iv-round__interviewer">{c.interviewerName}</div>
              )}
            </div>
          ) : (
            <span className="iv-nil">Not scheduled</span>
          )}
        </td>

        <td>
          {c.interviewDateTime ? (
            <div className="iv-round__date">
              {new Date(c.interviewDateTime).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
          ) : (
            <span className="iv-nil">Not scheduled</span>
          )}
          {getCandidateSlotUrls(c)
            .filter(
              (h) =>
                String(h.historyId) === String(getLatestInterviewHistoryId(c)),
            )
            .slice(0, 1)
            .map((h) => (
              <div key={h.historyId} style={{ marginTop: 4 }}>
                <a
                  className="iv-slot-link-chip"
                  href={h.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title={h.url}
                >
                  📎 {h.roundName}
                </a>
              </div>
            ))}
        </td>

        <td onClick={(e) => e.stopPropagation()}>
          <div className="iv-row-actions">
            <button
              className="iv-action-btn iv-action-btn--details"
              onClick={(e) => {
                e.stopPropagation();
                setDetailsCandidate(c);
              }}
              title="View candidate details"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <rect
                  x="1"
                  y="1"
                  width="11"
                  height="11"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <path
                  d="M3.5 4.5h6M3.5 6.5h6M3.5 8.5h4"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              Details
            </button>
            <button
              className="iv-action-btn iv-action-btn--timeline"
              onClick={() => setTimelineCandidate(c)}
              title="View hiring timeline"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <circle
                  cx="6.5"
                  cy="6.5"
                  r="5.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <path
                  d="M6.5 3.5v3l2 1.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              Timeline
            </button>

            {isApplied && (
              <>
                <button
                  className="iv-action-btn iv-action-btn--schedule"
                  onClick={() => setScheduleTarget(c)}
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <rect
                      x="1"
                      y="2"
                      width="11"
                      height="10"
                      rx="1.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M4 1v2M9 1v2M1 5h11"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6.5 7v2M5.5 8h2"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Move to Interview
                </button>
              </>
            )}

            {["SCHEDULED", "HOLD"].includes(st) && (
              <button
                className="iv-action-btn iv-action-btn--update-status"
                onClick={() => setStatusUpdateTarget(c)}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M2 6.5l3.5 3.5 5.5-5.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                update interview
              </button>
            )}

            {canReschedule && (
              <button
                className="iv-action-btn iv-action-btn--reschedule"
                onClick={() => setRescheduleTarget(c)}
                title="Reschedule slot"
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M11 2.5A5.5 5.5 0 1 0 12 7"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 2.5V5.5H9"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Reschedule Slot
              </button>
            )}

            {canSchedule && (
              <button
                className="iv-action-btn iv-action-btn--reschedule"
                onClick={() => setRescheduleTarget(c)}
                title="Schedule slot"
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M11 2.5A5.5 5.5 0 1 0 12 7"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 2.5V5.5H9"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Schedule Slot
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="iv-page">
      <div className="iv-page__header">
        <div className="iv-page__header-main">
          <div className="iv-page__title-wrap">
            <h2 className="iv-page__title">Interview Pipeline</h2>
            <span className="iv-page__count">{visible.length} interviews</span>
          </div>
          <p className="iv-page__sub">
            Follow every candidate from application to scheduled rounds in one
            Hiring Flow-style workspace.
          </p>
        </div>

        <div className="iv-view-toggle">
          <button
            className={`iv-view-toggle__btn${viewMode === "table" ? " iv-view-toggle__btn--active" : ""}`}
            onClick={() => setViewMode("table")}
            title="Table view"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="1"
                width="12"
                height="3"
                rx="1"
                fill="currentColor"
                opacity=".35"
              />
              <rect
                x="1"
                y="5.5"
                width="12"
                height="3"
                rx="1"
                fill="currentColor"
                opacity=".65"
              />
              <rect
                x="1"
                y="10"
                width="12"
                height="3"
                rx="1"
                fill="currentColor"
              />
            </svg>
            Table
          </button>
          <button
            className={`iv-view-toggle__btn${viewMode === "kanban" ? " iv-view-toggle__btn--active" : ""}`}
            onClick={() => setViewMode("kanban")}
            title="Kanban view"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="1"
                width="3"
                height="12"
                rx="1"
                fill="currentColor"
                opacity=".35"
              />
              <rect
                x="5.5"
                y="1"
                width="3"
                height="8"
                rx="1"
                fill="currentColor"
                opacity=".65"
              />
              <rect
                x="10"
                y="1"
                width="3"
                height="10"
                rx="1"
                fill="currentColor"
              />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      {!loading && (
        <div className="iv-stats-row">
          {pipelineStats.map((stat) => (
            <article
              key={stat.label}
              className={`iv-stat iv-stat--${stat.tone}`}
            >
              <span className="iv-stat__bar" />
              <div className="iv-stat__body">
                <div className="iv-stat__text">
                  <p className="iv-stat__label">{stat.label}</p>
                  <p className="iv-stat__sub">{stat.sub}</p>
                </div>
                <h3 className="iv-stat__value">{stat.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {viewMode === "table" && (
        <div className="iv-filters-wrap">
          <button
            className="iv-filters-wrap__toggle"
            onClick={() => setFiltersExpanded((p) => !p)}
          >
            <div className="iv-filters-wrap__toggle__left">
              <span className="iv-filters-wrap__toggle__label">Filters</span>
              {/* Active filter summary chips */}
              <div className="iv-filters-wrap__summary">
                {statusFilter !== "ALL" && (
                  <span className="iv-filters-wrap__chip">
                    Status: {statusFilter}
                  </span>
                )}
                {jobFilter !== "ALL" && (
                  <span className="iv-filters-wrap__chip">
                    Job:{" "}
                    {jobOptions.find((j) => j.id === jobFilter)?.title ||
                      jobFilter}
                  </span>
                )}
                {consultancyFilter !== "ALL" &&
                  consultancyFilter !== UNGROUPED_KEY && (
                    <span className="iv-filters-wrap__chip">
                      {consultancyOptions.find(
                        (c) => c.key === consultancyFilter,
                      )?.name || "Consultancy"}
                    </span>
                  )}
                {consultancyFilter === UNGROUPED_KEY && (
                  <span className="iv-filters-wrap__chip">Direct</span>
                )}
                {groupByConsultancy && (
                  <span className="iv-filters-wrap__chip">Grouped</span>
                )}
              </div>
              {hasActiveFilter && (
                <button
                  className="iv-clear-filters-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusFilter("ALL");
                    setJobFilter("ALL");
                    setConsultancyFilter("ALL");
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
            <span
              className={`iv-filters-wrap__toggle__arrow${filtersExpanded ? " iv-filters-wrap__toggle__arrow--open" : ""}`}
            >
              ▾
            </span>
          </button>

          {filtersExpanded && (
            <div className="iv-filters-wrap__body">
              {/* ── Status filter tabs ── */}
              <div className="iv-status-filter-bar">
                <span className="iv-flow-filter-bar__label">Status</span>
                {STATUS_FILTER_OPTIONS.filter(
                  (opt) =>
                    opt.value === "ALL" || (statusCounts[opt.value] ?? 0) > 0,
                ).map((opt) => (
                  <button
                    key={opt.value}
                    className={`iv-flow-chip iv-flow-chip--status${statusFilter === opt.value ? " iv-flow-chip--active" : ""}`}
                    onClick={() => setStatusFilter(opt.value)}
                  >
                    {opt.label}
                    <span className="iv-filter-tab__n">
                      {statusCounts[opt.value] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              {jobOptions.length > 0 && (
                <div className="iv-flow-filter-bar">
                  <span className="iv-flow-filter-bar__label">Job</span>
                  <button
                    className={`iv-flow-chip iv-flow-chip--job${jobFilter === "ALL" ? " iv-flow-chip--active" : ""}`}
                    onClick={() => setJobFilter("ALL")}
                  >
                    All Jobs{" "}
                    <span className="iv-filter-tab__n">
                      {candidates.length}
                    </span>
                  </button>
                  {jobOptions.map((j) => (
                    <button
                      key={j.id}
                      className={`iv-flow-chip iv-flow-chip--job${jobFilter === j.id ? " iv-flow-chip--active" : ""}`}
                      onClick={() => setJobFilter(j.id)}
                    >
                      {j.title}
                      <span className="iv-filter-tab__n">
                        {jobCounts[j.id] || 0}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {consultancyOptions.length > 0 && (
                <div className="iv-flow-filter-bar iv-consultancy-filter-bar">
                  <span className="iv-flow-filter-bar__label">Consultancy</span>
                  <button
                    className={`iv-flow-chip iv-flow-chip--consultancy${consultancyFilter === "ALL" ? " iv-flow-chip--active" : ""}`}
                    onClick={() => setConsultancyFilter("ALL")}
                  >
                    All{" "}
                    <span className="iv-filter-tab__n">
                      {candidates.length}
                    </span>
                  </button>
                  {consultancyOptions.map((co) => (
                    <button
                      key={co.key}
                      className={`iv-flow-chip iv-flow-chip--consultancy${consultancyFilter === co.key ? " iv-flow-chip--active" : ""}`}
                      onClick={() => setConsultancyFilter(co.key)}
                    >
                      {co.name}
                      <span className="iv-filter-tab__n">
                        {consultancyCounts[co.key] || 0}
                      </span>
                    </button>
                  ))}
                  {candidates.some((c) => !getConsultancyName(c)) && (
                    <button
                      className={`iv-flow-chip iv-flow-chip--consultancy${consultancyFilter === UNGROUPED_KEY ? " iv-flow-chip--active" : ""}`}
                      onClick={() => setConsultancyFilter(UNGROUPED_KEY)}
                    >
                      Direct
                      <span className="iv-filter-tab__n">
                        {
                          candidates.filter((c) => !getConsultancyName(c))
                            .length
                        }
                      </span>
                    </button>
                  )}
                </div>
              )}

              {consultancyOptions.length > 0 && (
                <div className="iv-group-toggle-bar">
                  <label className="iv-group-toggle">
                    <input
                      type="checkbox"
                      checked={groupByConsultancy}
                      onChange={(e) => {
                        setGroupByConsultancy(e.target.checked);
                        setCollapsedGroups(new Set());
                      }}
                    />
                    <span className="iv-group-toggle__label">
                      🗂 Group by Consultancy
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === "table" && (
        <>
          {/* Smart Filters + Sort row */}
          <div className="iv-toolbar-row">
            <div className="iv-toolbar">
              <div className="iv-sort-control">
                <div className="iv-sort-tabs">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`iv-flow-chip${
                        sortBy === opt.value ? " iv-flow-chip--active" : ""
                      }`}
                      onClick={() => setSortBy(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {hasActiveFilters && (
                  <button
                    className="cand-filter-clear"
                    onClick={() =>
                      setFilters({
                        skills: "",
                        salaryMax: "",
                        qualificationType: "",
                        qualification: "",
                      })
                    }
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Search bar */}
              <div className="iv-search-bar">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <circle
                    cx="6"
                    cy="6"
                    r="4.5"
                    stroke="#9ca3af"
                    strokeWidth="1.4"
                  />

                  <path
                    d="M10 10l3 3"
                    stroke="#9ca3af"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>

                <input
                  className="iv-search-input"
                  placeholder="Search by name, email, round, job, consultancy, source..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {search && (
                  <button
                    className="iv-search-clear"
                    onClick={() => setSearch("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="cand-filter-panel" style={{ marginBottom: 12 }}>
            <div className="cand-filter-panel__grid">
              <div className="cand-field">
                <label className="cand-field__label">
                  Skills (comma-separated)
                </label>
                <input
                  className="cand-input"
                  placeholder="e.g. Communication, Sales, Excel"
                  value={filters.skills}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, skills: e.target.value }))
                  }
                />
              </div>
              <div className="cand-field">
                <label className="cand-field__label">
                  Max Expected Salary (₹)
                </label>
                <input
                  className="cand-input"
                  type="number"
                  placeholder="e.g. 800000"
                  value={filters.salaryMax}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, salaryMax: e.target.value }))
                  }
                />
              </div>
              <div className="cand-field">
                <label className="cand-field__label">Qualification Type</label>
                <select
                  className="cand-input"
                  value={filters.qualificationType}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      qualificationType: e.target.value,
                      qualification: "",
                    }))
                  }
                >
                  <option value="">Any</option>
                  {[
                    ["SCHOOL", "School"],
                    ["PRE_UNIVERSITY", "Pre-University"],
                    ["BACHELOR", "Bachelor's Degree"],
                    ["MASTER", "Master's Degree"],
                    ["PHD", "PhD / Doctorate"],
                    ["OTHER", "Other"],
                  ].map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cand-field">
                <label className="cand-field__label">Qualification</label>
                <select
                  className="cand-input"
                  value={filters.qualification}
                  disabled={!filters.qualificationType}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      qualification: e.target.value,
                    }))
                  }
                >
                  <option value="">
                    {filters.qualificationType ? "Any" : "Select type first"}
                  </option>
                  {(
                    {
                      SCHOOL: ["10th Standard", "12th Standard"],
                      PRE_UNIVERSITY: ["Diploma", "ITI", "Polytechnic"],
                      BACHELOR: [
                        "B.E / B.Tech",
                        "B.Sc",
                        "B.Com",
                        "B.A",
                        "BBA",
                        "BCA",
                        "B.Arch",
                        "B.Pharm",
                        "MBBS",
                        "Other",
                      ],
                      MASTER: [
                        "M.E / M.Tech",
                        "M.Sc",
                        "M.Com",
                        "M.A",
                        "MBA",
                        "MCA",
                        "M.Pharm",
                        "MD",
                        "Other",
                      ],
                      PHD: ["Ph.D", "D.Sc", "Other"],
                      OTHER: ["Other"],
                    }[filters.qualificationType] || []
                  ).map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <p className="cand-filter-panel__hint">
                ✦ Candidates ranked by match score — skills, salary fit, and
                qualification.
              </p>
            )}
          </div>
        </>
      )}

      <BulkActionBar
        selectedCount={selectedIds.size}
        onSchedule={() => setBulkScheduleOpen(true)}
        onClear={() => setSelectedIds(new Set())}
      />

      {loading ? (
        <div className="iv-loader">
          <span className="iv-spin" /> Loading candidates...
        </div>
      ) : viewMode === "kanban" ? (
        flows.length === 0 ? (
          <div className="iv-empty">
            <span>🔀</span>
            <p>No hiring flows found</p>
            <small>Create a hiring flow first to use Kanban view.</small>
          </div>
        ) : (
          <KanbanBoard
            candidates={candidates}
            flows={flows}
            onSchedule={setScheduleTarget}
            onStatusUpdate={setStatusUpdateTarget}
            onReschedule={setRescheduleTarget}
            onMove={handleKanbanMove}
            onViewTimeline={setTimelineCandidate}
            onViewDetails={setDetailsCandidate}
            jobOptions={jobOptions}
            jobCounts={jobCounts}
          />
        )
      ) : visible.length === 0 ? (
        <div className="iv-empty">
          <span>🎯</span>
          <p>No candidates found</p>
          <small>
            {search || hasActiveFilter
              ? "Clear your filters."
              : "No one has applied yet."}
          </small>
        </div>
      ) : (
        <div className="iv-table-wrap">
          <table className="iv-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  {schedulableVisible.length > 0 && (
                    <input
                      type="checkbox"
                      className="iv-checkbox"
                      checked={allSchedulableSelected}
                      onChange={toggleSelectAll}
                      title={
                        allSchedulableSelected
                          ? "Deselect all schedulable"
                          : "Select all schedulable"
                      }
                    />
                  )}
                </th>
                <th>SL No</th>
                <th>Candidate</th>
                <th>Job</th>
                <th>Source / Consultancy</th>
                <th>Status</th>
                <th>Round / Interviewer</th>
                <th>Scheduled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupByConsultancy && groupedRows
                ? groupedRows.map((group) => {
                    const isOpen = !collapsedGroups.has(group.key);
                    const color = groupColor(group.key);
                    return (
                      <React.Fragment key={group.key}>
                        <ConsultancyGroupRow
                          label={group.label}
                          consultancyId={group.consultancyId}
                          count={group.candidates.length}
                          isOpen={isOpen}
                          onToggle={() => toggleGroup(group.key)}
                          color={color}
                        />
                        {isOpen &&
                          group.candidates.map((c, i) =>
                            renderCandidateRow(c, i),
                          )}
                      </React.Fragment>
                    );
                  })
                : visible
                    .slice(
                      (tablePage - 1) * TABLE_PAGE_SIZE,
                      tablePage * TABLE_PAGE_SIZE,
                    )
                    .map((c, i) =>
                      renderCandidateRow(
                        c,
                        (tablePage - 1) * TABLE_PAGE_SIZE + i,
                      ),
                    )}{" "}
            </tbody>
          </table>

          <TablePagination
            page={tablePage}
            total={visible.length}
            pageSize={TABLE_PAGE_SIZE}
            onChange={setTablePage}
          />
        </div>
      )}

      {scheduleTarget && (
        <ScheduleModal
          candidate={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          onDone={done}
        />
      )}

      {statusUpdateTarget && (
        <StatusUpdateModal
          candidate={statusUpdateTarget}
          flows={flows}
          onClose={() => setStatusUpdateTarget(null)}
          onDone={done}
        />
      )}

      {detailsCandidate && (
        <CandidateDetailsDrawer
          candidate={detailsCandidate}
          onClose={() => setDetailsCandidate(null)}
          onViewTimeline={() => setTimelineCandidate(detailsCandidate)}
        />
      )}

      {rescheduleTarget && (
        <RescheduleModal
          candidate={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onDone={() => {
            setRescheduleTarget(null);
            fetchAll();
          }}
        />
      )}

      {timelineCandidate && (
        <CandidateTimelinePanel
          candidate={timelineCandidate}
          flows={flows}
          onClose={() => setTimelineCandidate(null)}
          onRefresh={fetchAll}
        />
      )}

      {bulkScheduleOpen && selectedCandidates.length > 0 && (
        <BulkScheduleModal
          candidates={selectedCandidates}
          onClose={() => setBulkScheduleOpen(false)}
          onDone={done}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ScheduleModal
// ─────────────────────────────────────────────────────────────────────────────
const ScheduleModal = ({ candidate, onClose, onDone }) => {
  const preselectRound = candidate._preselectRound || null;
  const isApplied =
    getCandidateStatus(candidate) === "APPLIED" ||
    getCandidateStatus(candidate) === "NOT_SCHEDULED";
  const [rejecting, setRejecting] = useState(false);

  const [form, setForm] = useState({
    interviewRoundId: preselectRound
      ? String(preselectRound.interviewRoundId)
      : "",
    interviewDateTime: "",
    comments: "",
    sourceId: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  // ── Slot fetch state ──────────────────────────────────────────────────────
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const getResumeUrl = (c) => c.candidate?.resumeUrl || c.resumeUrl || "";
  const sourceList = candidate.sources || [];
  const interviewHistoryId = getLatestInterviewHistoryId(candidate);

  useEffect(() => {
    if (candidate.sources?.length >= 1) {
      setForm((prev) => ({
        ...prev,
        sourceId: String(candidate.sources[0].id),
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch available slots whenever jobPostId or roundId is known ──────────
  useEffect(() => {
    if (!interviewHistoryId) return;
    setLoadingSlots(true);
    setSlotError(null);
    setSlots([]);
    setSelectedSlotId(null);
    axios
      .get(`${AVAILABLE_SLOTS_API}/${interviewHistoryId}/available-slots`)
      .then((res) => {
        setSlots(res.data?.data || []);
      })
      .catch((err) => {
        setSlotError(
          err?.response?.data?.message || "Failed to load available slots.",
        );
      })
      .finally(() => setLoadingSlots(false));
  }, [interviewHistoryId]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.sourceId) e.sourceId = "Select a source";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleReject = async () => {
    if (!form.sourceId && sourceList.length > 0) {
      setErrors({ sourceId: "Select a source" });
      return;
    }
    setRejecting(true);
    try {
      const payload = {
        appliedCandidateHistoryId: Number(getCandidateId(candidate)),
        appliedCandidateHistorySourceId: Number(form.sourceId),
        hiringFlowId: candidate.hiringFlowId,
        comments: form.comments,
        status: "REJECTED",
      };
      await axios.put(MOVE_API, [payload]);
      toast.success(`${name} has been rejected.`);
      onDone();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to reject candidate.",
      );
    } finally {
      setRejecting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const histId = getCandidateId(candidate);
    try {
      const payload = {
        appliedCandidateHistoryId: Number(histId),
        appliedCandidateHistorySourceId: Number(form.sourceId),
        hiringFlowId: candidate.hiringFlowId,
        ...(selectedSlotId ? { slotId: Number(selectedSlotId) } : {}),
        ...(form.interviewDateTime && !selectedSlotId
          ? {
              interviewDateTime: new Date(form.interviewDateTime).toISOString(),
            }
          : {}),
        comments: form.comments,
        status: "MOVED",
      };
      const res = await axios.put(MOVE_API, [payload]);
      setResult(res.data.data || []);
      toast.success("Interview scheduled!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const fmtSlot = (d) =>
    d
      ? new Date(d).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const fmtEnd = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const name = getCandidateName(candidate);
  const email = getCandidateEmail(candidate);

  // ── Success screen ────────────────────────────────────────────────────────
  if (result) {
    const hasUrl = result.some((r) => r.url);
    return (
      <div className="iv-overlay" onClick={hasUrl ? undefined : onDone}>
        <div
          className="iv-modal iv-modal--success"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="iv-success-ring">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M8 18l7 7 13-13"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="iv-success-title">Interview Scheduled!</h3>
          <p className="iv-success-sub">{name} has been moved to interview.</p>
          {result.map((r, idx) => (
            <div key={idx} className="iv-result-rows">
              {r.interviewRoundName && (
                <div className="iv-result-row">
                  <span>Round</span>
                  <strong>{r.interviewRoundName}</strong>
                </div>
              )}
              {r.interviewerName && (
                <div className="iv-result-row">
                  <span>Interviewer</span>
                  <strong>{r.interviewerName}</strong>
                </div>
              )}
              {r.status && (
                <div className="iv-result-row">
                  <span>Status</span>
                  <Badge status={r.status} />
                </div>
              )}
              {r.message && (
                <div className="iv-result-row">
                  <span>Note</span>
                  <em className="iv-result-note">{r.message}</em>
                </div>
              )}
              {r.url && (
                <div className="iv-slot-url-block">
                  <div className="iv-slot-url-block__label">
                    📎 Candidate Slot Booking Link
                  </div>
                  <div className="iv-slot-url-block__row">
                    <span className="iv-slot-url-block__url">{r.url}</span>
                    <button
                      className={`iv-slot-url-block__copy${copiedIdx === idx ? " iv-slot-url-block__copy--done" : ""}`}
                      onClick={() => handleCopy(r.url, idx)}
                    >
                      {copiedIdx === idx ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="iv-slot-url-block__hint">
                    Share this link with the candidate so they can pick an
                    available interview slot.
                  </p>
                </div>
              )}
            </div>
          ))}
          <button className="iv-done-btn" onClick={onDone}>
            Done ✓
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="iv-overlay" onClick={onClose}>
      <div className="iv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="iv-modal__hd">
          <div>
            <h3 className="iv-modal__title">Schedule Interview</h3>
            <p className="iv-modal__sub">
              Move <strong>{name}</strong> to an interview round
              {preselectRound && (
                <span className="iv-modal__preselect">
                  {" "}
                  — dropping into: <em>{preselectRound.roundName}</em>
                </span>
              )}
            </p>
          </div>
          <button className="iv-modal__x" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="iv-cand-chip">
          <div
            className="iv-avatar iv-avatar--md"
            style={{ background: avatarColor(name) }}
          >
            {getInitials(name)}
          </div>
          <div>
            <div className="iv-cand-chip__name">{name}</div>
            <div className="iv-cand-chip__meta">
              {email}
              {getResumeUrl(candidate) && (
                <>
                  <a
                    href={getResumeUrl(candidate)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "#2563eb", fontWeight: 500 }}
                  >
                    Resume
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="iv-form-body">
          {/* ── Source selection ── */}
          {isApplied && (
            <div className="iv-field">
              <label className="iv-lbl">
                Source <span className="iv-req">*</span>
              </label>
              <select
                className="iv-sel"
                value={form.sourceId}
                onChange={(e) => set("sourceId", e.target.value)}
                disabled={sourceList.length === 1}
              >
                {sourceList.length > 1 && (
                  <option value="">Select source</option>
                )}
                {sourceList.map((s) => (
                  <option key={s.id || s.sourceId} value={s.id || s.sourceId}>
                    {s.sourceName}
                    {s.consultancyName ? ` · ${s.consultancyName}` : ""}
                  </option>
                ))}
              </select>
              {sourceList.length > 1 && (
                <span style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  This candidate has multiple sources — please select one.
                </span>
              )}
              {errors.sourceId && (
                <span className="iv-err">{errors.sourceId}</span>
              )}
            </div>
          )}

          {/* ── Available slots ── */}
          <div className="iv-field">
            <label className="iv-lbl">
              Available Slots
              {slots.length > 0 && (
                <span
                  style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}
                >
                  ({slots.length} available)
                </span>
              )}
            </label>

            {!interviewHistoryId ? (
              <div className="iv-reschedule-no-slots">
                <span>ℹ️</span>
                <p>No interview history found for this candidate.</p>
                <small>
                  Slots cannot be loaded without an interview history ID.
                </small>
              </div>
            ) : loadingSlots ? (
              <div className="iv-loader" style={{ padding: "20px 0" }}>
                <span className="iv-spin" /> Loading available slots...
              </div>
            ) : slotError ? (
              <div className="iv-reschedule-no-slots">
                <span>⚠️</span>
                <p>{slotError}</p>
              </div>
            ) : slots.length === 0 ? (
              <div className="iv-reschedule-no-slots">
                <span>📭</span>
                <p>No available slots for this round.</p>
                <small>
                  Ask the interviewer to open availability slots, or schedule
                  without a slot below.
                </small>
              </div>
            ) : (
              <div className="iv-slot-grid">
                {slots.map((slot) => {
                  const isSelected = String(selectedSlotId) === String(slot.id);
                  const isUnavailable =
                    slot.isScheduled === true ||
                    (slot.slotStatus || "").toUpperCase() !== "AVAILABLE";
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      disabled={isUnavailable}
                      className={`iv-slot-card${isSelected ? " iv-slot-card--selected" : ""}${isUnavailable ? " iv-slot-card--unavailable" : ""}`}
                      onClick={() =>
                        setSelectedSlotId(isSelected ? null : String(slot.id))
                      }
                    >
                      <div className="iv-slot-card__time">
                        {fmtSlot(slot.slotStartDateTime)}
                      </div>
                      <div className="iv-slot-card__end">
                        until {fmtEnd(slot.slotEndDateTime)}
                      </div>
                      {slot.interviewerName && (
                        <div className="iv-slot-card__interviewer">
                          👤 {slot.interviewerName}
                        </div>
                      )}
                      <div
                        className={`iv-slot-card__status iv-slot-card__status--${(slot.slotStatus || "available").toLowerCase()}`}
                      >
                        {slot.isScheduled
                          ? "Already Booked"
                          : slot.slotStatus === "AVAILABLE"
                            ? "✓ Available"
                            : slot.slotStatus}
                      </div>
                      {isSelected && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#2563eb",
                            fontWeight: 600,
                            marginTop: 4,
                          }}
                        >
                          ✓ Selected
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Hint when a slot is selected */}
            {selectedSlotId && (
              <p
                style={{
                  fontSize: 12,
                  color: "#16a34a",
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                ✅ Slot selected — it will be booked on submission.
              </p>
            )}

            {/* Fallback: no slot chosen but slots exist */}
            {!selectedSlotId && slots.length > 0 && (
              <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                Select a slot above, or leave unselected to schedule without
                assigning one.
              </p>
            )}
          </div>

          {/* ── Comments ── */}
          <div className="iv-field">
            <label className="iv-lbl">Comments / Instructions</label>
            <textarea
              className="iv-ta"
              rows={3}
              placeholder="Topics to assess, any special instructions..."
              value={form.comments}
              onChange={(e) => set("comments", e.target.value)}
            />
          </div>
        </div>

        <div className="iv-modal__ft">
          <button className="iv-ghost-btn" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="iv-primary-btn iv-primary-btn--danger"
            onClick={handleReject}
            disabled={submitting || rejecting}
            style={{ marginRight: 8 }}
          >
            {rejecting ? (
              <>
                <span className="iv-spin iv-spin--lt" /> Rejecting...
              </>
            ) : (
              "✕ Reject"
            )}
          </button>
          <button
            className="iv-primary-btn"
            onClick={handleSubmit}
            disabled={submitting || rejecting}
          >
            {submitting ? (
              <>
                <span className="iv-spin iv-spin--lt" /> Scheduling...
              </>
            ) : selectedSlotId ? (
              "Schedule & Book Slot →"
            ) : (
              "Schedule Interview →"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export { CandidateTimelinePanel, ScheduleModal };
export default InterviewPipeline;
